import { ServiceData } from "../types/service";

export const SERVICES: ServiceData[] = [
{
  id: "cultural-exchanges-conferences-events",
  title: "Cultural Exchanges, Conferences and Events",
  tagline: "Experience New Cultures, Build Networks and Access Global Opportunities",
  heroDescription:
    "TravEd Consult helps students, professionals, entrepreneurs, organisations and institutions participate in meaningful cultural exchange programmes, conferences, exhibitions, summits and international events.",
  image: "/images/services/cultural-exchanges-conferences-events.jpg",

  introduction: {
    title: "Cultural Exchanges, Conferences and Events",
    paragraphs: [
      "TravEd Consult helps students, professionals, entrepreneurs, organisations and institutions participate in meaningful cultural exchange programmes, conferences, exhibitions, summits and international events.",
      "We understand that travelling for an exchange programme or professional event involves much more than simply purchasing a ticket. Participants may need assistance identifying a credible opportunity, understanding the requirements, completing registration, preparing supporting documents, applying for a visa, arranging accommodation and planning the entire journey.",
      "Our role is to provide coordinated support from the initial search for an opportunity through to travel preparation and participation. We help clients select programmes and events that are relevant to their academic background, professional interests, business objectives and long-term development.",
      "Whether you want to attend an international conference, participate in a youth exchange, join a business delegation, present research, exhibit a product or organise an institutional visit, TravEd Consult helps you plan the experience with clarity and confidence."
    ]
  },

  offerSections: [
    {
      title: "Cultural Exchange Programmes",
      description:
        "Cultural exchange programmes allow participants to experience another country, interact with people from different backgrounds and gain a better understanding of other cultures, institutions and ways of life. These programmes can support personal growth, leadership development, language learning, professional exposure and international networking. They can also help participants develop confidence, independence, communication skills and a broader understanding of global issues. TravEd Consult helps clients identify suitable exchange programmes based on their age, educational background, professional experience, interests, preferred destination, available budget and intended outcome.",
      bullets: [
        "Student exchanges",
        "Youth leadership programmes",
        "Language and cultural immersion programmes",
        "Volunteer exchanges",
        "Entrepreneurship exchanges",
        "Professional visits",
        "Community-development programmes",
        "Educational tours",
        "Summer programmes",
        "Heritage experiences",
        "Institutional exchanges",
        "Programme objectives and eligibility assessment",
        "Duration and fee assessment",
        "Accommodation arrangements",
        "Travel responsibility guidance",
        "Expected outcome assessment",
        "Fully funded, partially funded, sponsored and self-funded programme guidance"
      ]
    },

    {
      title: "Cultural Preparation and Orientation",
      description:
        "Participating in an international exchange requires more than academic or professional preparation. Participants must also be ready to adapt to unfamiliar customs, communication styles, food, weather, social expectations and daily routines. TravEd Consult provides cultural orientation to help participants understand what to expect at their destination and prepares them for possible challenges such as homesickness, language barriers, culture shock and difficulty adjusting to a new environment.",
      bullets: [
        "Local customs",
        "Appropriate behaviour",
        "Communication",
        "Dress",
        "Religious practices",
        "Host-family expectations",
        "Respectful interaction with people from different backgrounds",
        "Homesickness preparation",
        "Language-barrier preparation",
        "Culture-shock preparation",
        "Adjustment to a new environment",
        "Representing themselves and Ghana positively"
      ]
    },

    {
      title: "Conferences and Professional Events",
      description:
        "Conferences, exhibitions, workshops, summits and professional events provide valuable opportunities to learn, share ideas and build relationships. TravEd Consult assists clients in identifying relevant events that support their academic, professional or business goals and helps participants assess whether an event is suitable for their background, field of work, organisation or long-term objectives.",
      bullets: [
        "Academic conferences",
        "Business summits",
        "Entrepreneurship forums",
        "Technology events",
        "Education fairs",
        "Trade exhibitions",
        "Leadership conferences",
        "Investment forums",
        "Healthcare events",
        "Policy discussions",
        "Research symposiums",
        "Networking events",
        "Youth forums",
        "International conventions",
        "Event structure assessment",
        "Registration category guidance",
        "Participation fee guidance",
        "Programme schedule review",
        "Speaker session information",
        "Networking opportunity assessment",
        "Travel requirement guidance"
      ]
    },

    {
      title: "Conference and Event Research",
      description:
        "There are many conferences and events advertised online, but not all of them are credible or suitable. TravEd Consult conducts reasonable research into an event before recommending it to a client. We review the organiser, official event platform, venue, programme schedule, registration information, speaker details, previous editions and available contact information. We also consider whether the event has a clear professional purpose and whether it is genuinely connected to the participant's academic background, career, business or organisation.",
      bullets: [
        "Organiser review",
        "Official event platform review",
        "Venue review",
        "Programme schedule review",
        "Registration information review",
        "Speaker details review",
        "Previous event editions review",
        "Available contact information review",
        "Professional purpose assessment",
        "Relevance to academic background, career, business or organisation",
        "Further confirmation guidance where information is incomplete or unclear",
        "Protection against registering for events simply because they offer an invitation letter"
      ]
    },

    {
      title: "Registration and Application Support",
      description:
        "Once a suitable conference, exchange programme or event has been identified, TravEd Consult guides the client through the registration or application process. We help clients understand the required documents and ensure that the information provided is clear, accurate and consistent. For group participation, we may assist with collecting participant details, selecting the appropriate registration category, confirming payment arrangements and coordinating communication with the event organiser.",
      bullets: [
        "Online registration forms",
        "Professional profiles",
        "Academic CVs",
        "Motivation statements",
        "Registration fee guidance",
        "Official invitation-letter requests",
        "Document requirement guidance",
        "Information accuracy and consistency review",
        "Group participant detail collection",
        "Registration category selection",
        "Payment arrangement confirmation",
        "Event organiser communication",
        "Professional presentation of applicant background, experience, interests and expected contribution"
      ]
    },

    {
      title: "Academic Conference Participation",
      description:
        "TravEd Consult supports students, researchers, lecturers and academics who wish to attend or present at international conferences. Academic participation may involve submitting a research abstract, preparing a conference paper, presenting a poster, joining a panel discussion or attending sessions for professional development. We help participants identify conferences that are relevant to their research area and professional goals.",
      bullets: [
        "Research abstract submission guidance",
        "Conference paper preparation guidance",
        "Poster presentation guidance",
        "Panel discussion preparation",
        "Professional-development conference participation",
        "Conference identification",
        "Registration guidance",
        "Invitation-letter requests",
        "Travel-grant opportunity guidance",
        "Supporting documentation",
        "Professional biography review",
        "Academic CV review",
        "Abstract structure review",
        "Presentation outline review",
        "Conference correspondence review"
      ]
    },

    {
      title: "Speakers, Panellists and Exhibitors",
      description:
        "Professionals, business owners and subject-matter experts may attend conferences as speakers, panellists, exhibitors or special guests. TravEd Consult helps such participants prepare for both the travel and professional aspects of the event.",
      bullets: [
        "Speaker biographies",
        "Event correspondence",
        "Presentation planning",
        "Exhibition enquiries",
        "Booth arrangements",
        "Promotional materials",
        "Meeting schedules",
        "Product displays",
        "Printed materials",
        "Transportation of permitted exhibition items",
        "Communication with event organisers",
        "Professional preparation for communicating ideas",
        "Work promotion",
        "Relationship and network building"
      ]
    },

    {
      title: "Business Delegations and Trade Missions",
      description:
        "TravEd Consult supports entrepreneurs, companies, professional associations, NGOs and institutions participating in business delegations, investment forums and trade missions. These trips are designed to help participants explore new markets, meet potential partners, learn about international business practices and identify investment or collaboration opportunities.",
      bullets: [
        "Event research",
        "Delegate registration",
        "Group travel coordination",
        "Accommodation",
        "Ground transportation",
        "Meeting schedules",
        "Company profiles",
        "Business presentations",
        "Partnership proposals",
        "Introductory messages",
        "Agriculture",
        "Technology",
        "Education",
        "Healthcare",
        "Construction",
        "Tourism",
        "Renewable energy",
        "Manufacturing",
        "Creative industries",
        "Finance",
        "Logistics",
        "International trade",
        "Clear travel objectives",
        "Target-person and organisation identification",
        "Information-gathering objectives",
        "Partnership objectives"
      ]
    },

    {
      title: "Institutional Exchange Programmes",
      description:
        "Schools, universities, companies, NGOs, churches, associations and government-related institutions may wish to organise structured exchanges or professional visits. TravEd Consult helps such organisations plan programmes that combine education, cultural interaction, professional development and practical exposure.",
      bullets: [
        "University visits",
        "Professional meetings",
        "Company visits",
        "Workshops",
        "Community engagement",
        "Cultural activities",
        "Guided tours",
        "Programme purpose definition",
        "Participant identification",
        "Destination selection",
        "Schedule development",
        "Invitation assistance",
        "Accommodation coordination",
        "Transportation",
        "Group travel",
        "Participant orientation",
        "Programme coordination",
        "Budget planning",
        "Duration planning"
      ]
    },

    {
      title: "Educational and Professional Study Visits",
      description:
        "Study visits provide participants with the opportunity to observe how institutions, businesses and communities operate in another environment. These visits can be organised for students, professionals, entrepreneurs, researchers and institutional teams.",
      bullets: [
        "Universities",
        "Schools",
        "Research centres",
        "Companies",
        "Factories",
        "Hospitals",
        "Government agencies",
        "Innovation hubs",
        "Business incubators",
        "NGOs",
        "Tourism facilities",
        "Community-development projects",
        "Travel coordination",
        "Communication with institutions or organisations to be visited"
      ]
    },

    {
      title: "Volunteer and Community Engagement Programmes",
      description:
        "TravEd Consult also helps clients explore legitimate volunteer and community-engagement opportunities. Before recommending a volunteer programme, we consider the clarity of the participant's role, programme supervision, accommodation, safety arrangements and the credibility of the host organisation.",
      bullets: [
        "Education",
        "Healthcare support",
        "Environmental protection",
        "Entrepreneurship",
        "Youth development",
        "Agriculture",
        "Cultural preservation",
        "Community development",
        "Participant-role assessment",
        "Programme supervision assessment",
        "Accommodation assessment",
        "Safety-arrangement assessment",
        "Host-organisation credibility assessment",
        "Visa requirement guidance",
        "Permit requirement guidance",
        "Health-check guidance",
        "Insurance guidance",
        "Background-check guidance",
        "Responsible volunteering guidance"
      ]
    },

    {
      title: "Networking and Professional Preparation",
      description:
        "One of the greatest benefits of attending a conference or exchange programme is the opportunity to build relationships. TravEd Consult helps participants prepare for meaningful networking so they can return from the event with useful knowledge, contacts and possible opportunities rather than simply attending sessions.",
      bullets: [
        "Professional introductions",
        "Business profiles",
        "Personal biographies",
        "Short explanation of professional work",
        "Company profiles",
        "Pitch materials",
        "Business cards",
        "Partnership proposals",
        "Identifying relevant speakers",
        "Identifying relevant organisations",
        "Identifying exhibitors",
        "Identifying delegates",
        "Professional introductions",
        "Meeting requests",
        "Relevant questions",
        "Contact-information exchange",
        "Post-event follow-up"
      ]
    },

    {
      title: "Invitation and Supporting Documents",
      description:
        "Depending on the event or programme, participants may require an official invitation letter, registration confirmation, programme schedule, payment receipt or participation letter. TravEd Consult guides clients on requesting and organising these documents and helps applicants ensure that all documents present a clear and consistent explanation of the trip.",
      bullets: [
        "Official invitation letters",
        "Registration confirmations",
        "Programme schedules",
        "Payment receipts",
        "Participation letters",
        "Proof of employment",
        "Business-registration documents",
        "Academic certificates",
        "Sponsorship letters",
        "Leave approval",
        "Accommodation confirmation",
        "Financial documents",
        "Flight reservations",
        "Statement explaining the purpose of travel",
        "Document consistency guidance"
      ]
    },

    {
      title: "Visa Facilitation",
      description:
        "TravEd Consult provides visa facilitation for clients travelling to cultural exchange programmes, conferences and events. We explain the visa requirements for the destination and provide a personalised document checklist.",
      bullets: [
        "Visa requirement explanation",
        "Personalised document checklist",
        "Visa-form guidance",
        "Appointment scheduling",
        "Financial-document review",
        "Invitation-letter review",
        "Travel-itinerary preparation",
        "Interview coaching where required",
        "Purpose-of-event explanation",
        "Relevance to academic or professional background",
        "Source-of-funding explanation",
        "Plans-after-programme explanation"
      ]
    },

    {
      title: "Flights, Accommodation and Transportation",
      description:
        "TravEd Consult can also assist participants with the practical travel arrangements required to attend the programme or event. We help compare flight options and identify accommodation and transportation arrangements that are connected to the event schedule.",
      bullets: [
        "Flight comparison",
        "Price comparison",
        "Baggage allowance comparison",
        "Travel-duration comparison",
        "Transit-requirement comparison",
        "Ticket-condition comparison",
        "Hotels",
        "Conference accommodation",
        "Serviced apartments",
        "Hostels",
        "University residences",
        "Group lodging",
        "Airport transfers",
        "Car rental",
        "Group transportation",
        "Daily movement between accommodation and event venue",
        "Group room allocation",
        "Flight schedule coordination",
        "Participant communication"
      ]
    },

    {
      title: "Event Travel Itinerary",
      description:
        "TravEd Consult prepares a clear itinerary to help the participant remain organised. A properly prepared itinerary helps participants manage their time and reduces confusion during the trip.",
      bullets: [
        "Flight details",
        "Transit information",
        "Airport transfers",
        "Accommodation address",
        "Event venue",
        "Registration time",
        "Conference schedule",
        "Planned meetings",
        "Cultural activities",
        "Return-flight information",
        "Business meeting times",
        "Company visits",
        "Networking events",
        "Free periods"
      ]
    },

    {
      title: "Pre-Departure Orientation",
      description:
        "Before travelling, participants receive practical preparation for the journey and the event. We review essential travel and event information and provide guidance on destination-specific practical, cultural and professional considerations.",
      bullets: [
        "Passport review",
        "Visa review",
        "Flight details",
        "Baggage allowance",
        "Accommodation information",
        "Event documents",
        "Transportation arrangements",
        "Destination weather",
        "Currency",
        "Communication",
        "Cultural expectations",
        "Professional etiquette",
        "Personal safety",
        "Emergency contacts",
        "Appropriate dressing",
        "Professional introductions",
        "Networking behaviour",
        "Session planning",
        "Presentation preparation",
        "Post-event follow-up",
        "Important-document preparation",
        "Physical and electronic document copies"
      ]
    },

    {
      title: "Funding and Sponsorship Opportunities",
      description:
        "Some cultural exchange programmes and conferences provide funding, scholarships, travel grants, registration waivers or sponsorship. TravEd Consult may help clients identify available opportunities and understand the eligibility requirements.",
      bullets: [
        "Funding opportunities",
        "Scholarships",
        "Travel grants",
        "Registration waivers",
        "Sponsorship opportunities",
        "Eligibility requirements",
        "Application-document preparation guidance",
        "Programme-organiser funding",
        "University funding",
        "Employer funding",
        "Professional-association funding",
        "Foundation funding",
        "Government-institution funding"
      ]
    },

    {
      title: "Event Hosting and Coordination in Ghana",
      description:
        "TravEd Consult also supports international organisations, institutions and professional groups planning events or exchange programmes in Ghana. We can help visiting groups develop a programme that combines professional activities with meaningful exposure to Ghanaian culture, history, business and communities.",
      bullets: [
        "Venue research",
        "Accommodation",
        "Airport pickup",
        "Transportation",
        "Delegate registration",
        "Cultural activities",
        "Educational visits",
        "Business visits",
        "Local tours",
        "Ghanaian culture exposure",
        "Ghanaian history exposure",
        "Ghanaian business exposure",
        "Community exposure",
        "Programme customisation according to participant numbers",
        "Programme objectives",
        "Available budget",
        "Preferred locations",
        "Programme duration"
      ]
    }
  ],

  areasSupported: {
    title: "Areas We Support",
    items: [
      "Cultural Exchange Programmes",
      "Student Exchanges",
      "Youth Leadership Programmes",
      "Language and Cultural Immersion",
      "Volunteer Exchanges",
      "Entrepreneurship Exchanges",
      "Academic Conferences",
      "Business Summits",
      "Entrepreneurship Forums",
      "Technology Events",
      "Education Fairs",
      "Trade Exhibitions",
      "Leadership Conferences",
      "Investment Forums",
      "Healthcare Events",
      "Policy Discussions",
      "Research Symposiums",
      "Networking Events",
      "Youth Forums",
      "International Conventions",
      "Business Delegations",
      "Trade Missions",
      "Institutional Exchange Programmes",
      "Educational Study Visits",
      "Professional Study Visits",
      "Volunteer and Community Engagement",
      "Event Hosting and Coordination in Ghana"
    ]
  },

  process: {
    title: "Our Process",

    steps: [
      {
        number: "01",
        title: "Initial Consultation",
        description:
          "We begin by understanding the type of programme or event you want to attend, your background, objectives, preferred destination and budget."
      },
      {
        number: "02",
        title: "Opportunity Research",
        description:
          "We research suitable programmes, exchanges, conferences or events that align with your profile and goals."
      },
      {
        number: "03",
        title: "Assessment and Recommendation",
        description:
          "We review the available information, including credibility, eligibility, costs, programme structure and expected benefits."
      },
      {
        number: "04",
        title: "Registration or Application",
        description:
          "We guide you through the registration process and help organise the required supporting documents."
      },
      {
        number: "05",
        title: "Visa Preparation",
        description:
          "Where necessary, we provide visa facilitation and interview preparation."
      },
      {
        number: "06",
        title: "Travel Coordination",
        description:
          "We help arrange flights, accommodation, transportation and other relevant travel services."
      },
      {
        number: "07",
        title: "Pre-Departure Preparation",
        description:
          "You receive travel, cultural and professional orientation before departure."
      },
      {
        number: "08",
        title: "Post-Programme Guidance",
        description:
          "After the programme, we encourage participants to evaluate the experience, follow up with contacts and apply the knowledge gained."
      }
    ]
  },

  whyChoose: {
    title: "Why Choose TravEd Consult?",

    items: [
      {
        title: "Integrated Opportunity and Travel Support",
        description:
          "TravEd Consult provides both opportunity guidance and travel coordination. This means clients do not have to work with different providers for event research, registration, visa facilitation, flights and accommodation."
      },
      {
        title: "Personalised Recommendations",
        description:
          "Our recommendations are based on the client's academic background, professional interests, business goals and budget."
      },
      {
        title: "Honest Guidance",
        description:
          "We provide honest guidance about programme credibility, funding, costs, documentation and visa responsibilities."
      },
      {
        title: "Cultural and Professional Preparation",
        description:
          "We prepare participants to gain real value from the experience through cultural orientation, networking preparation and post-event follow-up."
      },
      {
        title: "Coordinated Support",
        description:
          "Our support extends from identifying the right opportunity through registration, documentation, visa preparation, travel coordination and pre-departure preparation."
      }
    ]
  },

  importantNotice: {
    title: "Important Notice",
    paragraphs: [
      "TravEd Consult provides opportunity research, registration assistance, visa facilitation and travel coordination directly or through third-party service providers.",
      "We do not control the decisions of event organisers, programme hosts, sponsors, airlines, hotels, embassies or immigration authorities.",
      "Participation, invitation, funding and visa approval cannot be guaranteed.",
      "Programme dates, venues, speakers, activities, travel costs and accommodation arrangements may change.",
      "Clients are responsible for providing accurate information, submitting genuine documents, meeting programme requirements, reviewing payment and cancellation terms, obtaining appropriate insurance and complying with the laws of the destination country.",
      "An invitation letter does not automatically qualify a person for a visa. It is only one part of the overall application.",
      "TravEd Consult does not issue visas and cannot guarantee approval. The final decision is made by the relevant embassy, consulate, immigration authority or visa officer.",
      "All information and documents submitted must be truthful, genuine and verifiable.",
      "TravEd Consult cannot guarantee that an applicant will receive a grant, scholarship or sponsorship."
    ]
  },

  cta: {
    title: "Expand Your World with TravEd Consult",
    description:
      "Cultural exchanges, conferences and international events can open doors to knowledge, partnerships, personal growth and professional opportunities. TravEd Consult helps you identify the right opportunity, prepare your documents, plan your journey and participate with confidence.",
    buttonText: "Contact TravEd Consult"
  }
},

{
  id: "business-travel-vacations-tours",
  title: "Business Travel, Vacations and Tours",
  tagline: "Local and International Travel Planned Around You",
  heroDescription:
    "Whether you are travelling for business, relaxation, exploration or a special occasion, proper planning can make the difference between a stressful journey and a memorable experience. TravEd Consult provides personalised business travel, vacation and tour services for individuals, families, groups, companies and institutions.",
  image: "/images/services/business-travel-vacations-tours.jpg",

  introduction: {
    title: "Business Travel, Vacations and Tours",
    paragraphs: [
      "TravEd Consult provides personalised business travel, vacation and tour services for individuals, families, groups, companies and institutions.",
      "We help our clients plan and organise local and international trips based on their purpose, preferred destination, travel dates, budget and personal requirements.",
      "From flights and accommodation to transportation, activities and travel documentation, our team coordinates the important details so you can travel with greater comfort and confidence."
    ]
  },

  offerSections: [
    {
      title: "Personalised Travel Consultation",
      description:
        "Every journey is different. We begin by understanding why you are travelling and the type of experience you want. Based on this information, we prepare suitable travel options and a practical plan for your journey.",
      bullets: [
        "Purpose of travel",
        "Preferred destination",
        "Travel dates and duration",
        "Number and ages of travellers",
        "Preferred travel style",
        "Accommodation preferences",
        "Planned activities",
        "Transportation needs",
        "Dietary or accessibility requirements",
        "Available budget",
        "Visa and entry requirements"
      ]
    },

    {
      title: "Business and Corporate Travel",
      description:
        "Business travel requires efficiency, flexibility and careful coordination. TravEd Consult helps professionals, entrepreneurs, corporate teams, NGOs and institutions organise local and international business trips so travellers can concentrate on their meetings and professional objectives while we assist with the travel arrangements.",
      bullets: [
        "Domestic business travel",
        "International business travel",
        "Professional travel planning",
        "Corporate travel coordination",
        "Business-trip organisation",
        "Travel arrangements for professional objectives"
      ]
    },

    {
      title: "Corporate Flight Arrangements",
      description:
        "We assist business travellers and corporate teams in researching and coordinating suitable flight arrangements. We explain the conditions attached to each ticket before booking, including change fees, refund restrictions and baggage rules.",
      bullets: [
        "Domestic and international flight searches",
        "Economy options",
        "Premium economy options",
        "Business-class options",
        "Flexible and refundable fare options",
        "Direct and connecting flight comparisons",
        "Baggage allowance checks",
        "Seat-selection guidance",
        "Multi-city travel planning",
        "Group flight reservations",
        "Flight-change guidance",
        "Cancellation guidance",
        "Final itinerary preparation"
      ]
    },

    {
      title: "Business Accommodation",
      description:
        "We help business travellers identify suitable accommodation based on convenience, comfort and proximity to their activities.",
      bullets: [
        "Business hotels",
        "Serviced apartments",
        "Conference hotels",
        "Short-stay apartments",
        "Executive accommodation",
        "Group accommodation",
        "Long-stay corporate housing",
        "Accommodation near meeting venues",
        "Accommodation near business districts",
        "Accommodation near airports",
        "Accommodation near conference centres",
        "Accommodation near transportation routes"
      ]
    },

    {
      title: "Airport Transfers and Ground Transportation",
      description:
        "We provide guidance and booking assistance for airport transfers and ground transportation required during business and professional trips.",
      bullets: [
        "Airport pickup and drop-off",
        "Hotel transfers",
        "Chauffeur services",
        "Car rental",
        "Vehicle hire with a driver",
        "Intercity transportation",
        "Group buses and coaches",
        "Daily business transportation",
        "Conference transportation",
        "VIP transportation arrangements"
      ]
    },

    {
      title: "Business Itinerary Coordination",
      description:
        "A well-organised business itinerary helps travellers manage their time effectively. We coordinate the major travel, accommodation, meeting and professional activities into a clear itinerary containing major reservations, timings, addresses and relevant contact details.",
      bullets: [
        "Flight schedules",
        "Hotel check-in and check-out",
        "Airport transfers",
        "Meeting locations",
        "Conference schedules",
        "Local transportation",
        "Rest periods",
        "Business meals",
        "Optional leisure activities",
        "Return travel arrangements",
        "Major reservation details",
        "Travel timings",
        "Addresses",
        "Relevant contact details"
      ]
    },

    {
      title: "Corporate Travel Account Support",
      description:
        "For organisations with regular travel requirements, our support can be tailored to the size, budget and travel frequency of the organisation.",
      bullets: [
        "Dedicated corporate travel contact",
        "Travel request coordination",
        "Booking approval procedures",
        "Staff travel profiles",
        "Company travel-policy guidance",
        "Preferred airline options",
        "Preferred hotel options",
        "Group and departmental travel planning",
        "Consolidated booking records",
        "Trip-cost summaries",
        "Travel-document reminders",
        "Traveller communication",
        "Post-trip service review"
      ]
    },

    {
      title: "Executive and VIP Travel",
      description:
        "Executives and senior professionals may require additional privacy, flexibility and personalised support. Our executive travel assistance is designed around these requirements.",
      bullets: [
        "Premium flight options",
        "Priority accommodation selection",
        "Private airport transfers",
        "Flexible ticket options",
        "Personalised itineraries",
        "Lounge-access guidance",
        "Meet-and-assist service arrangements",
        "Dedicated travel coordination",
        "Special requests",
        "Preference management"
      ]
    },

    {
      title: "Meetings, Conferences and Corporate Events",
      description:
        "TravEd Consult supports organisations arranging meetings, conferences, workshops, retreats, exhibitions and incentive trips. We can also help organisations combine official business activities with carefully selected cultural, recreational or team-building experiences.",
      bullets: [
        "Destination research",
        "Venue research",
        "Conference hotel selection",
        "Group flight coordination",
        "Delegate accommodation",
        "Airport transfers",
        "Group transportation",
        "Meeting-room arrangements",
        "Team-building activities",
        "Corporate retreat planning",
        "Local sightseeing",
        "Cultural experiences",
        "Event travel itineraries",
        "Guest travel",
        "Speaker travel",
        "Pre-event travel communication"
      ]
    },

    {
      title: "Vacation and Holiday Planning",
      description:
        "A vacation should provide time to relax, explore and create meaningful memories. TravEd Consult helps clients organise holidays that match their interests, travel style and budget.",
      bullets: [
        "Individual travel",
        "Couples travel",
        "Family holidays",
        "Groups of friends",
        "Corporate holiday groups",
        "Birthday trips",
        "Anniversary trips",
        "Honeymoon travel",
        "Graduation trips",
        "Family reunions",
        "Weekend getaways",
        "Seasonal holidays"
      ]
    },

    {
      title: "Customised Holiday Packages",
      description:
        "Rather than offering the same holiday to everyone, we develop travel plans around the client. The exact inclusions will be clearly stated before the client confirms the package.",
      bullets: [
        "Return flights",
        "Hotel or resort accommodation",
        "Airport transfers",
        "Daily breakfast or meal plans",
        "Guided tours",
        "Attraction tickets",
        "Excursions",
        "Transportation",
        "Travel insurance assistance",
        "Visa facilitation",
        "Destination information",
        "Personalised travel itinerary"
      ]
    },

    {
      title: "Types of Vacation Experiences",
      description:
        "TravEd Consult can help clients plan different types of vacation experiences based on their interests, preferred travel style and budget.",
      bullets: [
        "Beach holidays",
        "City breaks",
        "Cultural holidays",
        "Heritage tours",
        "Nature and wildlife trips",
        "Adventure holidays",
        "Wellness and relaxation trips",
        "Shopping holidays",
        "Family holidays",
        "Romantic getaways",
        "Educational tours",
        "Religious and pilgrimage travel",
        "Festival and event trips",
        "Multi-city holidays",
        "Luxury travel experiences",
        "Budget-friendly vacations"
      ]
    },

    {
      title: "Local Tours in Ghana",
      description:
        "TravEd Consult helps individuals, families, schools, organisations and international visitors explore Ghana through organised local tours.",
      bullets: [
        "Cultural and heritage tours",
        "City tours",
        "Nature and adventure tours",
        "Beach and relaxation tours",
        "Arts, crafts and culinary experiences",
        "Festival and event tours",
        "Educational and institutional tours"
      ]
    },

    {
      title: "Cultural and Heritage Tours",
      description:
        "Our cultural and heritage tour experiences help travellers explore Ghanaian traditions, history, communities and cultural heritage.",
      bullets: [
        "Ghanaian traditions and customs",
        "Historical communities",
        "Museums and monuments",
        "Cape Coast and Elmina heritage experiences",
        "Traditional leadership and royal history",
        "Slave-route and diaspora heritage experiences",
        "Cultural performances",
        "Local storytelling and history"
      ]
    },

    {
      title: "City Tours",
      description:
        "TravEd Consult can organise city experiences that allow visitors and residents to explore Ghanaian cities, commercial areas, creative spaces and landmarks.",
      bullets: [
        "Accra city experiences",
        "Kumasi city experiences",
        "Cape Coast and Elmina tours",
        "Tema city tours",
        "Regional capital tours",
        "Markets and commercial districts",
        "Art galleries and creative spaces",
        "Historical and modern landmarks"
      ]
    },

    {
      title: "Nature and Adventure Tours",
      description:
        "We help travellers explore Ghana's natural and outdoor attractions through organised nature and adventure experiences.",
      bullets: [
        "National parks",
        "Waterfalls",
        "Botanical gardens",
        "Mountain and hiking experiences",
        "Forest reserves",
        "Canopy walks",
        "Wildlife experiences",
        "Lakes and rivers",
        "Eco-tourism attractions"
      ]
    },

    {
      title: "Beach and Relaxation Tours",
      description:
        "TravEd Consult organises beach and coastal experiences for individuals, families and groups seeking relaxation and leisure.",
      bullets: [
        "Beach resorts",
        "Coastal getaways",
        "Weekend relaxation trips",
        "Group beach experiences",
        "Family beach holidays",
        "Waterfront activities"
      ]
    },

    {
      title: "Arts, Crafts and Culinary Experiences",
      description:
        "Clients can explore Ghanaian creative traditions, crafts and food through organised cultural experiences.",
      bullets: [
        "Kente-weaving communities",
        "Adinkra-making experiences",
        "Bead-making centres",
        "Pottery and craft villages",
        "Ghanaian cooking experiences",
        "Local food tours",
        "Art exhibitions",
        "Creative workshops"
      ]
    },

    {
      title: "Festival and Event Tours",
      description:
        "We may organise travel around selected cultural and entertainment events. All event availability and schedules remain subject to the relevant organisers.",
      bullets: [
        "Traditional festivals",
        "Independence celebrations",
        "Easter activities",
        "PANAFEST and Emancipation events",
        "December festivities",
        "Music and arts events",
        "Regional cultural celebrations"
      ]
    },

    {
      title: "Educational and Institutional Tours",
      description:
        "Our local tour service can support educational, institutional, professional and community-based trips. Risk assessment, supervision requirements and age-appropriate activities should be considered for school and youth groups.",
      bullets: [
        "School excursions",
        "University trips",
        "Research visits",
        "Organisational retreats",
        "Staff team-building trips",
        "Church and association tours",
        "Community visits",
        "Professional field trips",
        "International student experiences",
        "Risk assessment",
        "Supervision requirements",
        "Age-appropriate activities"
      ]
    },

    {
      title: "International Tours and Vacations",
      description:
        "TravEd Consult helps clients plan international trips for holidays, business, family visits, events and cultural experiences.",
      bullets: [
        "Destination research",
        "Flight arrangements",
        "Hotel reservations",
        "Airport transfers",
        "Tour and activity bookings",
        "Travel insurance guidance",
        "Visa facilitation",
        "Entry-requirement guidance",
        "Local transportation planning",
        "Multi-city itineraries",
        "Travel-budget preparation",
        "Destination orientation"
      ]
    },

    {
      title: "Destination Selection",
      description:
        "Clients who have not selected a destination can receive guidance based on their travel purpose, preferences, budget and intended experience. We provide realistic options rather than recommending a destination based only on popularity.",
      bullets: [
        "Travel purpose",
        "Preferred weather",
        "Available budget",
        "Visa requirements",
        "Travel duration",
        "Flight accessibility",
        "Personal interests",
        "Family suitability",
        "Available attractions",
        "Preferred travel experience"
      ]
    },

    {
      title: "Multi-Country and Multi-City Trips",
      description:
        "TravEd Consult can assist travellers who want to visit more than one city or country during the same journey. The itinerary is designed to reduce unnecessary travel time and avoid unrealistic connections.",
      bullets: [
        "Travel routes",
        "Connecting flights",
        "Train and bus travel",
        "Accommodation in each destination",
        "Transfer times",
        "Visa and transit considerations",
        "Attraction schedules",
        "Baggage requirements",
        "Entry dates",
        "Departure dates"
      ]
    },

    {
      title: "Group Travel",
      description:
        "Group travel requires careful coordination because every traveller may have different expectations and needs. A designated group representative may be required to coordinate decisions and communication.",
      bullets: [
        "Family vacations",
        "Friendship-group trips",
        "School and educational tours",
        "Church trips",
        "Association tours",
        "Corporate retreats",
        "Conference groups",
        "Alumni trips",
        "Destination celebrations",
        "Cultural and heritage groups",
        "Group flight enquiries",
        "Room allocation",
        "Group accommodation",
        "Transportation",
        "Group activities",
        "Tour guides",
        "Meal arrangements",
        "Payment schedules",
        "Traveller information collection",
        "Group communication",
        "Final itinerary preparation"
      ]
    },

    {
      title: "Flight Booking and Ticketing Support",
      description:
        "We assist clients in comparing suitable flight options based on price, schedule, airline, baggage allowance, transit requirements and ticket conditions. Before payment, clients receive the important booking details for review.",
      bullets: [
        "Fare comparison",
        "Travel-duration comparison",
        "Number-of-stops comparison",
        "Departure-time comparison",
        "Arrival-time comparison",
        "Airline comparison",
        "Baggage allowance",
        "Transit airport",
        "Ticket flexibility",
        "Refund conditions",
        "Change conditions",
        "Booking-detail review before payment"
      ]
    },

    {
      title: "Hotel and Accommodation Reservations",
      description:
        "We help travellers find accommodation suitable for their destination, budget and purpose and assist them in comparing important accommodation conditions.",
      bullets: [
        "Hotels",
        "Resorts",
        "Guesthouses",
        "Serviced apartments",
        "Vacation rentals",
        "Villas",
        "Hostels",
        "Conference hotels",
        "Family rooms",
        "Group accommodation",
        "Location comparison",
        "Room-type comparison",
        "Facilities comparison",
        "Meal-plan comparison",
        "Guest reviews",
        "Transportation access",
        "Check-in requirements",
        "Cancellation terms",
        "Taxes and additional charges"
      ]
    },

    {
      title: "Airport Transfers, Transportation and Car Rental",
      description:
        "TravEd Consult can assist with local and international transportation arrangements. Vehicle availability, driver arrangements and service conditions depend on the selected provider and destination.",
      bullets: [
        "Airport pickup",
        "Airport drop-off",
        "Private transfers",
        "Shared transfers",
        "Hotel transportation",
        "Car rental",
        "Vehicle hire with a driver",
        "Group coaches",
        "Intercity transportation",
        "Tour transportation"
      ]
    },

    {
      title: "Visa and Travel-Document Guidance",
      description:
        "Where a visa or travel authorisation is required, TravEd Consult can provide general facilitation and application guidance. TravEd Consult does not issue visas or guarantee approval. Visa decisions are made solely by the relevant embassy, consulate, immigration authority or authorised visa officer.",
      bullets: [
        "Entry-requirement checks",
        "Personalised document checklists",
        "Visa-form guidance",
        "Appointment guidance",
        "Financial-document guidance",
        "Travel-itinerary preparation",
        "Accommodation confirmation guidance",
        "Interview preparation where applicable",
        "Application follow-up guidance"
      ]
    },

    {
      title: "Travel Insurance Assistance",
      description:
        "Travel insurance can provide protection against certain unexpected circumstances, depending on the policy purchased. Clients must review the policy benefits, exclusions, limits and claim requirements before purchasing insurance.",
      bullets: [
        "Medical emergencies",
        "Trip cancellation",
        "Trip interruption",
        "Delayed travel",
        "Lost or delayed baggage",
        "Personal accidents",
        "Emergency evacuation",
        "Other eligible travel risks",
        "Policy benefit review",
        "Policy exclusion review",
        "Policy limit review",
        "Claim-requirement review"
      ]
    },

    {
      title: "Special Travel Requirements",
      description:
        "Travellers should inform us early about any requirements that may affect their arrangements. Requests remain subject to the airline, hotel, transport provider or tour operator's policies and availability.",
      bullets: [
        "Mobility or accessibility assistance",
        "Travelling with children",
        "Elderly travellers",
        "Dietary requirements",
        "Medical considerations",
        "Pregnancy-related airline requirements",
        "Extra baggage",
        "Sports equipment",
        "Special seating requests",
        "Group supervision",
        "Unaccompanied-minor arrangements"
      ]
    },

    {
      title: "Pre-Travel Orientation",
      description:
        "Before departure, clients receive guidance relevant to their journey. Business travellers may also receive guidance on local business etiquette, transportation and meeting-day planning.",
      bullets: [
        "Passport and visa checks",
        "Flight and baggage information",
        "Airport procedures",
        "Transit requirements",
        "Hotel details",
        "Transportation arrangements",
        "Destination weather",
        "Local currency and payment methods",
        "Local customs and etiquette",
        "Emergency contact information",
        "Personal safety",
        "Important travel documents",
        "Communication and internet access",
        "Health and entry requirements",
        "Local business etiquette",
        "Meeting-day planning"
      ]
    },

    {
      title: "Travel Support and Disruption Assistance",
      description:
        "Unexpected situations such as flight delays, cancellations, missed connections and hotel changes can affect a journey. Depending on the package and service provider, our team may assist clients with understanding notifications, contacting providers and reviewing alternative arrangements.",
      bullets: [
        "Understanding airline notifications",
        "Contacting the relevant service provider",
        "Reviewing alternative flight options",
        "Rebooking guidance",
        "Hotel-extension enquiries",
        "Transfer adjustments",
        "Itinerary changes",
        "Locating relevant emergency contacts",
        "Communicating changes to group travellers"
      ]
    }
  ],

  areasSupported: {
    title: "Areas We Support",
    items: [
      "Business Travel",
      "Corporate Travel",
      "Executive and VIP Travel",
      "Meetings and Conferences",
      "Corporate Events",
      "Corporate Retreats",
      "Incentive Trips",
      "Vacation Planning",
      "Holiday Packages",
      "Beach Holidays",
      "City Breaks",
      "Cultural Holidays",
      "Heritage Tours",
      "Nature and Wildlife Trips",
      "Adventure Holidays",
      "Wellness and Relaxation Trips",
      "Shopping Holidays",
      "Family Holidays",
      "Romantic Getaways",
      "Educational Tours",
      "Religious and Pilgrimage Travel",
      "Festival and Event Trips",
      "Multi-City Holidays",
      "Luxury Travel",
      "Budget-Friendly Vacations",
      "Local Tours in Ghana",
      "International Tours and Vacations",
      "Group Travel",
      "Educational and Institutional Tours",
      "Flight Booking and Ticketing",
      "Hotel and Accommodation Reservations",
      "Airport Transfers and Transportation",
      "Visa and Travel-Document Guidance",
      "Travel Insurance Assistance",
      "Pre-Travel Orientation",
      "Travel Support and Disruption Assistance"
    ]
  },

  process: {
    title: "Our Process",

    steps: [
      {
        number: "01",
        title: "Travel Consultation",
        description:
          "We discuss your purpose, destination, dates, group size, preferences and budget."
      },
      {
        number: "02",
        title: "Research and Planning",
        description:
          "Our team researches suitable flights, accommodation, transportation and activities."
      },
      {
        number: "03",
        title: "Travel Proposal",
        description:
          "You receive a proposed itinerary with available options, estimated costs, inclusions and exclusions."
      },
      {
        number: "04",
        title: "Review and Selection",
        description:
          "We discuss the proposal and make reasonable adjustments based on your feedback."
      },
      {
        number: "05",
        title: "Reservation and Payment",
        description:
          "Once you approve the arrangements, the selected services are reserved in accordance with the provider's payment terms."
      },
      {
        number: "06",
        title: "Confirmation and Documentation",
        description:
          "You receive the available booking confirmations, receipts, itinerary and important travel information."
      },
      {
        number: "07",
        title: "Pre-Travel Briefing",
        description:
          "We review your arrangements and explain what to expect before departure."
      },
      {
        number: "08",
        title: "Travel Support",
        description:
          "Where included, we provide guidance when changes or difficulties affect your confirmed arrangements."
      },
      {
        number: "09",
        title: "Post-Trip Feedback",
        description:
          "We may contact you after your journey to review your experience and improve future services."
      }
    ]
  },

  whyChoose: {
    title: "Why Choose TravEd Consult?",

    items: [
      {
        title: "One Travel Partner",
        description:
          "We bring flights, accommodation, transportation, tours, visa facilitation and travel preparation together under one coordinated service."
      },
      {
        title: "Personalised Planning",
        description:
          "Your trip is developed around your purpose, interests, budget and personal requirements."
      },
      {
        title: "Local and International Support",
        description:
          "We help Ghanaian travellers explore destinations abroad while also helping local and international clients experience Ghana."
      },
      {
        title: "Business and Leisure Expertise",
        description:
          "Our services are designed for both professional travellers and people seeking memorable vacation experiences."
      },
      {
        title: "Transparent Travel Proposals",
        description:
          "We clearly communicate the available options, package inclusions, exclusions and major booking conditions before confirmation."
      },
      {
        title: "Carefully Selected Options",
        description:
          "We consider convenience, safety, location, cost and service conditions when presenting travel options."
      },
      {
        title: "Practical Destination Guidance",
        description:
          "We prepare clients for their destination rather than only completing reservations."
      },
      {
        title: "Continued Communication",
        description:
          "We keep clients informed throughout the planning and booking process."
      }
    ]
  },

  importantNotice: {
    title: "Important Notice",
    paragraphs: [
      "TravEd Consult provides travel planning, reservation assistance, tour coordination and related facilitation services directly or through selected service providers.",
      "We do not own or operate airlines, hotels, attractions, embassies, insurance companies or transportation providers unless clearly stated.",
      "Flight schedules, fares, hotel availability, attraction access, visa requirements, insurance terms and transportation arrangements may change without notice.",
      "All bookings are subject to the terms, conditions, cancellation rules and refund policies of the relevant provider.",
      "TravEd Consult does not issue visas and cannot guarantee visa approval. Visa decisions remain entirely at the discretion of the relevant immigration authority.",
      "TravEd Consult does not determine insurance claims or guarantee that a claim will be approved.",
      "Travellers are responsible for providing accurate personal information, holding a valid passport, meeting visa and entry requirements, reviewing booking details before payment, following destination laws and regulations, complying with airline and accommodation policies, arriving at the airport on time, declaring relevant health or accessibility requirements and reviewing applicable travel advisories.",
      "All event availability and schedules remain subject to the relevant organisers.",
      "Vehicle availability, driver arrangements and service conditions depend on the selected provider and destination.",
      "Requests for special travel requirements remain subject to the airline, hotel, transport provider or tour operator's policies and availability.",
      "Emergency assistance is subject to the selected provider, travel package, operating hours and destination.",
      "Flight fares are not confirmed until the airline or authorised booking provider issues the ticket."
    ]
  },

  cta: {
    title: "Plan Your Next Journey with TravEd Consult",
    description:
      "Whether you are attending an important business meeting, planning a family holiday, exploring Ghana or travelling internationally, TravEd Consult is ready to help you organise the journey.",
    buttonText: "Contact TravEd Consult"
  }
},

{
  id: "flight-booking-accommodation-travel-arrangements",
  title: "Flight Booking, Accommodation and Travel Arrangements",
  tagline: "Helping You Prepare for a Safe and Comfortable Journey",
  heroDescription:
    "Securing admission and receiving your student visa are major achievements, but preparing for your journey is equally important. TravEd Consult helps students organise their flight, accommodation, airport arrival and other essential travel arrangements, making the transition from Ghana to their study destination smooth, safe and well organised.",
  image: "/images/services/flight-booking-accommodation-travel-arrangements.jpg",

  introduction: {
    title: "Flight Booking, Accommodation and Travel Arrangements",
    paragraphs: [
      "Securing admission and receiving your student visa are major achievements, but preparing for your journey is equally important.",
      "At TravEd Consult, we help students organise their flight, accommodation, airport arrival and other essential travel arrangements. Our goal is to make your transition from Ghana to your study destination smooth, safe and well organised.",
      "We understand that travelling abroad for the first time can be exciting but also overwhelming. From selecting a suitable flight to finding a comfortable place to stay, our team provides practical guidance at every stage of your journey."
    ]
  },

  offerSections: [
    {
      title: "Flight Search and Booking Support",
      description:
        "We help students identify suitable flight options based on their destination, travel date, budget and personal preferences. Before making a reservation, we help you understand the complete journey, including the number of stops, transit countries, airport changes and total travel time.",
      bullets: [
        "Researching available flight options",
        "Comparing airline fares",
        "Reviewing direct and connecting flights",
        "Checking flight duration and arrival times",
        "Comparing baggage allowances",
        "Reviewing transit and layover periods",
        "Explaining ticket conditions",
        "Assisting with flight reservations",
        "Reviewing the final travel itinerary",
        "Providing guidance on online and airport check-in",
        "Understanding number of stops",
        "Understanding transit countries",
        "Checking airport changes",
        "Reviewing total travel time",
        "Guidance on avoiding non-refundable tickets before receiving the required visa or travel authorisation"
      ]
    },

    {
      title: "Travel Date Planning",
      description:
        "Choosing the right travel date helps ensure that students arrive early enough to settle into their accommodation and prepare for school. TravEd Consult helps students select a practical travel period that aligns with their university and immigration requirements.",
      bullets: [
        "Programme start date",
        "University registration period",
        "International student orientation",
        "Accommodation move-in date",
        "Visa validity period",
        "Recommended arrival date",
        "Flight availability",
        "Possible fare changes",
        "Time required to settle before classes begin"
      ]
    },

    {
      title: "Student Accommodation Support",
      description:
        "Finding safe, comfortable and affordable accommodation is an important part of preparing to study abroad. TravEd Consult assists students in exploring accommodation options based on their university location, budget, lifestyle and personal preferences. We do not own or directly operate student accommodation. Our role is to help students research available housing options, understand the booking process and make informed decisions before committing to a property.",
      bullets: [
        "Researching available housing options",
        "Understanding accommodation booking processes",
        "Comparing accommodation options",
        "University accommodation",
        "Private student accommodation",
        "Temporary accommodation",
        "Accommodation based on university location",
        "Accommodation based on budget",
        "Accommodation based on lifestyle",
        "Accommodation based on personal preferences"
      ]
    },

    {
      title: "Accommodation Needs Assessment",
      description:
        "We begin by understanding your housing needs and preferences so that we can focus on accommodation options that are more suitable for your academic and personal needs.",
      bullets: [
        "Monthly accommodation budget",
        "Distance from your university",
        "Preferred room type",
        "Private or shared living preferences",
        "Transportation access",
        "Availability of essential facilities",
        "Preferred move-in date",
        "Length of stay",
        "Personal safety considerations",
        "Special accommodation requirements"
      ]
    },

    {
      title: "University Accommodation Guidance",
      description:
        "Many universities provide accommodation for international students through halls of residence or on-campus housing. We help students understand the available options and the requirements associated with university accommodation.",
      bullets: [
        "Available university residences",
        "Application procedures",
        "Accommodation deadlines",
        "Room types",
        "Meal plan options",
        "Included facilities",
        "Payment arrangements",
        "Distance from classrooms and campus facilities",
        "University residence rules",
        "Move-in and move-out dates"
      ]
    },

    {
      title: "Private Student Accommodation",
      description:
        "Where university accommodation is unavailable or unsuitable, we help students explore private housing options and compare the available choices before making a decision.",
      bullets: [
        "Purpose-built student accommodation",
        "Shared student apartments",
        "Private rooms",
        "Studio apartments",
        "Homestay arrangements",
        "Shared houses",
        "Temporary accommodation",
        "Hotels for initial arrival",
        "Short-stay apartments for initial arrival"
      ]
    },

    {
      title: "Room Type Selection",
      description:
        "Accommodation providers may offer different room and property types. We guide students in understanding the general differences in privacy, facilities, cost and shared responsibilities so they can select an option that suits them.",
      bullets: [
        "Shared rooms",
        "Private rooms",
        "En-suite rooms",
        "Non-en-suite rooms",
        "Studio apartments",
        "Shared apartments",
        "University halls",
        "Homestays",
        "Privacy comparison",
        "Facilities comparison",
        "Cost comparison",
        "Shared-responsibility considerations"
      ]
    },

    {
      title: "Location and Accessibility Guidance",
      description:
        "The cheapest accommodation may not always be the most convenient. We help students consider location, transportation, daily travel requirements and general safety when evaluating accommodation.",
      bullets: [
        "Distance from the university",
        "Walking time to campus",
        "Access to buses, trains or other transportation",
        "Transportation costs",
        "Distance from supermarkets",
        "Distance from healthcare facilities",
        "Neighbourhood environment",
        "Travel time during winter or bad weather",
        "Access to part-time employment areas",
        "General safety considerations"
      ]
    },

    {
      title: "Accommodation Budget Planning",
      description:
        "Accommodation costs may include more than monthly rent. We help students understand the possible expenses associated with their housing so they can prepare a realistic accommodation budget.",
      bullets: [
        "Monthly or weekly rent",
        "Security deposit",
        "Advance rent",
        "Booking fees",
        "Utility bills",
        "Electricity and heating",
        "Water charges",
        "Internet",
        "Laundry",
        "Transportation",
        "Furnishing and household items",
        "Tenant insurance",
        "Maintenance charges",
        "Identifying which services are covered by rent",
        "Understanding usage limits where applicable"
      ]
    },

    {
      title: "Accommodation Comparison",
      description:
        "We assist students in comparing suitable properties before making payment. Students are encouraged to compare several options and evaluate the overall value and conditions of each property.",
      bullets: [
        "Rental cost",
        "Room size",
        "Distance from campus",
        "Property condition",
        "Included furniture",
        "Internet availability",
        "Laundry facilities",
        "Kitchen facilities",
        "Study areas",
        "Security arrangements",
        "Utility costs",
        "Contract length",
        "Cancellation conditions",
        "Move-in requirements"
      ]
    },

    {
      title: "Tenancy and Rental Agreement Guidance",
      description:
        "A tenancy agreement is a legally binding document. We provide general guidance on reviewing important areas so students can understand the terms before signing. Where legal interpretation is required, students may need to seek advice from a qualified legal professional in the destination country.",
      bullets: [
        "Rental amount",
        "Payment schedule",
        "Security deposit",
        "Contract duration",
        "Guarantor requirements",
        "Cancellation policy",
        "Refund conditions",
        "Responsibility for utility bills",
        "Property rules",
        "Damage and repair responsibilities",
        "Notice period",
        "Early termination conditions",
        "Move-in and move-out procedures",
        "Understanding the final agreement issued by the property owner or accommodation provider"
      ]
    },

    {
      title: "Accommodation Verification and Payment Safety",
      description:
        "International students can be targeted by accommodation scams, particularly when booking from outside the destination country. We encourage students to verify the property provider, understand the offer and obtain appropriate booking documentation before making payment.",
      bullets: [
        "Confirming the identity of the property provider",
        "Using recognised accommodation platforms",
        "Verifying the property address",
        "Requesting clear photographs or a virtual viewing",
        "Reviewing the rental agreement",
        "Confirming what is included in the rent",
        "Obtaining official payment instructions",
        "Avoiding unexplained cash payments",
        "Keeping receipts and booking confirmations",
        "Avoiding payments to unverified personal accounts",
        "Confirming cancellation conditions",
        "Confirming refund conditions",
        "Understanding the offer before payment",
        "Obtaining appropriate booking documentation"
      ]
    },

    {
      title: "Accommodation Booking Support",
      description:
        "Once a suitable option has been identified, we guide students through the booking process and help them understand the important details of their accommodation arrangements.",
      bullets: [
        "Reviewing booking requirements",
        "Completing accommodation forms",
        "Preparing requested identification documents",
        "Understanding deposit requirements",
        "Confirming payment arrangements",
        "Reviewing booking confirmation",
        "Checking the move-in date",
        "Confirming the property address",
        "Recording the accommodation provider's contact details"
      ]
    },

    {
      title: "Temporary Accommodation Support",
      description:
        "In some cases, permanent accommodation may not be available before the student's arrival. We may provide guidance on temporary options that can provide a place to stay while completing viewings or finalising a longer-term rental.",
      bullets: [
        "Hotels",
        "Hostels",
        "Short-stay apartments",
        "University guest accommodation",
        "Approved homestays",
        "Temporary student residences",
        "Initial-arrival accommodation",
        "Accommodation while completing property viewings",
        "Accommodation while finalising a longer-term rental"
      ]
    },

    {
      title: "Move-In Preparation",
      description:
        "Before travelling, we help students prepare for their accommodation move-in and understand the practical steps involved in taking possession of their room or apartment.",
      bullets: [
        "Confirming the move-in date and time",
        "Contacting the property manager",
        "Understanding key collection procedures",
        "Preparing required identification",
        "Confirming deposit and rent payments",
        "Checking available furniture",
        "Preparing essential household items",
        "Understanding property rules",
        "Recording the condition of the room",
        "Reporting existing damage",
        "Obtaining emergency contacts",
        "Obtaining maintenance contacts",
        "Taking photographs of the room or apartment when moving in"
      ]
    },

    {
      title: "Airport Pickup and Arrival Arrangements",
      description:
        "Arriving in a new country can be difficult, particularly for students travelling internationally for the first time. We help students prepare a clear plan for moving from the airport to their accommodation.",
      bullets: [
        "University airport pickup services",
        "Private airport transfer options",
        "Bus routes",
        "Train routes",
        "Public transport routes",
        "Taxi services",
        "Ride-hailing services",
        "Estimated transportation costs",
        "Property directions",
        "Arrival contact details",
        "Late-night arrival considerations",
        "Emergency transportation options",
        "Safe travel practices",
        "University airport-pickup registration guidance",
        "University airport-pickup deadline guidance"
      ]
    },

    {
      title: "Baggage and Packing Guidance",
      description:
        "Airlines have different rules regarding checked luggage, cabin baggage and restricted items. We help students understand the relevant baggage requirements and prepare practically for their journey.",
      bullets: [
        "Checked baggage allowance",
        "Hand luggage limits",
        "Additional baggage charges",
        "Oversized baggage rules",
        "Restricted and prohibited items",
        "Liquids",
        "Electronic devices",
        "Packing for the destination's weather",
        "Labelling luggage",
        "Securing personal belongings",
        "Packing essential documents",
        "Preparing a practical student packing list",
        "Keeping important travel and academic documents in hand luggage"
      ]
    },

    {
      title: "Transit and Layover Guidance",
      description:
        "Some journeys require students to pass through one or more countries before reaching their final destination. We help students understand transit procedures and requirements before travelling.",
      bullets: [
        "Connecting flight procedures",
        "Transit visa requirements",
        "Airport terminal changes",
        "Baggage transfer arrangements",
        "Security checks",
        "Immigration procedures",
        "Long layovers",
        "Boarding gate changes",
        "Missed flight connections",
        "Overnight transit arrangements",
        "Confirming whether a transit visa is required"
      ]
    },

    {
      title: "Pre-Departure Orientation",
      description:
        "TravEd Consult provides pre-departure orientation to help students understand what to expect before leaving Ghana and after arriving at their study destination. Our orientation helps students prepare for the academic, cultural, financial and personal adjustments associated with studying abroad.",
      bullets: [
        "Travel preparation",
        "Academic preparation",
        "Cultural adjustment",
        "Financial preparation",
        "Personal adjustment",
        "Life abroad preparation",
        "Arrival preparation",
        "University transition guidance"
      ]
    },

    {
      title: "Travel Document Preparation",
      description:
        "We guide students on the documents they may need to carry. The required documents may differ depending on the destination and the student's personal circumstances.",
      bullets: [
        "Valid passport",
        "Student visa or study permit",
        "University admission letter",
        "Enrolment documents",
        "Tuition payment receipt",
        "Accommodation confirmation",
        "Financial evidence",
        "Sponsorship or scholarship documents",
        "Academic certificates",
        "Medical documents",
        "Flight itinerary",
        "Travel insurance information",
        "Emergency contact details"
      ]
    },

    {
      title: "Airport and Immigration Preparation",
      description:
        "Students may be asked questions by immigration or border officials when they arrive. We help students prepare to explain their travel and study arrangements clearly and truthfully.",
      bullets: [
        "Purpose of the journey",
        "Programme they will study",
        "Institution they will attend",
        "Accommodation arrangements",
        "How their studies will be funded",
        "Duration of the programme",
        "Plans after completing their studies",
        "Clear and respectful communication with immigration officials",
        "Truthful responses to immigration questions"
      ]
    },

    {
      title: "Cultural Adjustment",
      description:
        "Moving to another country may involve differences in language, weather, food, communication and social behaviour. Our cultural preparation helps students understand these differences and prepare for life in a new environment.",
      bullets: [
        "Understanding cultural differences",
        "Respecting local laws and customs",
        "Communicating with people from different backgrounds",
        "Managing homesickness",
        "Building social connections",
        "Seeking support when necessary",
        "Adjusting to independent living",
        "Maintaining communication with family",
        "Understanding that adjustment takes time",
        "Identifying support available through the university and local community"
      ]
    },

    {
      title: "Academic Preparation",
      description:
        "Students may experience different teaching styles, assessment methods and classroom expectations. Our orientation helps students understand important academic practices before and after arrival.",
      bullets: [
        "University registration",
        "Course enrolment",
        "Class attendance",
        "Assignment deadlines",
        "Academic integrity",
        "Referencing and plagiarism",
        "Group assignments",
        "Communicating with lecturers",
        "Using student support services",
        "Managing study time"
      ]
    },

    {
      title: "Financial Preparation",
      description:
        "Students need a realistic plan for managing their expenses abroad. We provide general guidance to help students prepare for the financial aspects of studying and living in another country.",
      bullets: [
        "Preparing a student budget",
        "Carrying appropriate travel funds",
        "Using bank cards overseas",
        "Opening a local bank account",
        "Paying tuition fees",
        "Managing accommodation expenses",
        "Understanding currency differences",
        "Keeping emergency funds",
        "Avoiding financial scams",
        "Monitoring daily expenses"
      ]
    },

    {
      title: "Health, Safety and Well-Being",
      description:
        "Our pre-departure guidance also covers practical health, safety and well-being considerations for students preparing to live abroad.",
      bullets: [
        "Health insurance requirements",
        "Accessing healthcare services",
        "Carrying prescribed medication",
        "Registering with a local doctor",
        "Emergency contact numbers",
        "Personal safety",
        "Protecting important documents",
        "Avoiding scams",
        "Following local laws",
        "Contacting the university during emergencies"
      ]
    }
  ],

  areasSupported: {
    title: "Areas We Support",
    items: [
      "Flight Search and Booking",
      "Travel Date Planning",
      "Student Accommodation",
      "University Accommodation",
      "Private Student Accommodation",
      "Temporary Accommodation",
      "Room Type Selection",
      "Accommodation Comparison",
      "Accommodation Budget Planning",
      "Tenancy and Rental Agreement Guidance",
      "Accommodation Verification",
      "Accommodation Booking Support",
      "Move-In Preparation",
      "Airport Pickup and Arrival",
      "Baggage and Packing Guidance",
      "Transit and Layover Guidance",
      "Travel Document Preparation",
      "Airport and Immigration Preparation",
      "Cultural Adjustment",
      "Academic Preparation",
      "Financial Preparation",
      "Health, Safety and Well-Being",
      "Pre-Departure Orientation"
    ]
  },

  process: {
    title: "Our Process",

    steps: [
      {
        number: "01",
        title: "Travel and Accommodation Consultation",
        description:
          "We review your destination, programme start date, visa status, budget, travel preferences and accommodation needs."
      },
      {
        number: "02",
        title: "Flight and Housing Research",
        description:
          "We research suitable flight routes and help you explore accommodation options near your university."
      },
      {
        number: "03",
        title: "Options Comparison",
        description:
          "We help you compare flights and accommodation based on cost, location, convenience, safety and booking conditions."
      },
      {
        number: "04",
        title: "Booking Guidance",
        description:
          "We guide you through the reservation process and help you understand the relevant payment and cancellation terms."
      },
      {
        number: "05",
        title: "Document and Itinerary Review",
        description:
          "We review your travel itinerary, accommodation confirmation and essential travel documents."
      },
      {
        number: "06",
        title: "Pre-Departure Orientation",
        description:
          "You receive practical guidance on travel, immigration, accommodation, cultural adjustment, finances and student life."
      },
      {
        number: "07",
        title: "Arrival Preparation",
        description:
          "We help you prepare for airport arrival, transportation, accommodation check-in and university registration."
      }
    ]
  },

  whyChoose: {
    title: "Why Choose TravEd Consult?",

    items: [
      {
        title: "Personalised Support",
        description:
          "Our recommendations are based on your destination, budget, academic schedule and personal needs."
      },
      {
        title: "First-Time Traveller Guidance",
        description:
          "We explain flight procedures, baggage rules, airport transit, accommodation booking and arrival arrangements in a simple and practical way."
      },
      {
        title: "Accommodation Assistance",
        description:
          "We help students explore housing options and understand rental costs, locations, room types and booking conditions."
      },
      {
        title: "Complete Travel Preparation",
        description:
          "Our services connect your admission, visa facilitation, flight booking, accommodation and pre-departure preparation into one organised process."
      },
      {
        title: "Safety and Convenience",
        description:
          "We help students avoid unnecessary confusion and make more informed travel and accommodation decisions."
      }
    ]
  },

  importantNotice: {
    title: "Important Notice",
    paragraphs: [
      "TravEd Consult does not own or operate airlines, accommodation facilities or transportation companies.",
      "Our role is to provide information, booking assistance and general guidance.",
      "Flight and accommodation availability, prices, refunds, cancellations and service conditions are determined by the relevant airline, property owner, university, accommodation platform or service provider.",
      "Students are responsible for reviewing and accepting all booking details, tenancy agreements, payment terms and cancellation conditions before making payment.",
      "All travellers must comply with the immigration, transit, customs, health and entry requirements of their destination and any country through which they travel.",
      "A tenancy agreement is a legally binding document. Where legal interpretation is required, students may need to seek advice from a qualified legal professional in the destination country.",
      "Students should verify accommodation providers and avoid sending money to unverified personal accounts.",
      "Students should keep important travel and academic documents in their hand luggage rather than checked baggage.",
      "Students must answer immigration and border questions truthfully, clearly and respectfully.",
      "Accommodation providers, airlines, universities and other service providers may have their own requirements, deadlines, payment conditions and cancellation policies."
    ]
  },

  cta: {
    title: "Prepare for Your Journey",
    description:
      "Let TravEd Consult help you organise your flight, find suitable accommodation and prepare confidently for your new academic journey.",
    buttonText: "Contact TravEd Consult"
  }
},

{
  id: "visa-facilitation",
  title: "Visa Facilitation",
  tagline: "Prepare Your Student Visa Application with Confidence",
  heroDescription:
    "Receiving admission is an important achievement, but obtaining the required student visa is the next major step in your study-abroad journey. TravEd Consult provides professional visa facilitation services to help students understand the requirements, prepare the necessary documents and confidently complete the application process.",
  image: "/images/services/visa-facilitation.jpg",

  introduction: {
    title: "Visa Facilitation",
    paragraphs: [
      "Receiving admission is an important achievement, but obtaining the required student visa is the next major step in your study-abroad journey.",
      "TravEd Consult provides professional visa facilitation services to help students understand the requirements, prepare the necessary documents and confidently complete the application process.",
      "Visa requirements differ from one country to another. Our team provides country-specific guidance to help you submit a complete, accurate and well-organised application."
    ]
  },

  offerSections: [
    {
      title: "Visa Application Guidance",
      description:
        "We explain the visa requirements for your chosen study destination and guide you through each stage of the process.",
      bullets: [
        "Student visa requirement assessment",
        "Visa application form guidance and review",
        "Personalised document checklist",
        "Application timeline and deadline planning",
        "Visa fee and submission guidance",
        "Visa appointment scheduling assistance"
      ]
    },

    {
      title: "Document Preparation and Review",
      description:
        "The quality and consistency of your supporting documents can significantly affect your application. We help you organise and review the documents required for your student visa application.",
      bullets: [
        "Valid passport",
        "University admission or acceptance letter",
        "Academic certificates and transcripts",
        "Financial documents and bank statements",
        "Sponsorship documents",
        "Proof of tuition payment or deposit",
        "Accommodation information",
        "Study plan or personal statement",
        "Employment and professional documents",
        "Other documents required by the relevant embassy or immigration authority",
        "Guidance on obtaining certified documents",
        "Guidance on obtaining approved translations"
      ]
    },

    {
      title: "Financial Documentation Guidance",
      description:
        "Applicants are generally required to demonstrate that they have sufficient funds to pay their tuition fees and living expenses. Our team helps you understand the financial requirements and organise your financial evidence clearly and consistently.",
      bullets: [
        "Financial requirements for your destination",
        "Acceptable sources of funding",
        "Bank statement requirements",
        "Sponsorship documentation",
        "Proof of income and financial capacity",
        "Organisation of financial evidence",
        "Consistency of financial documentation",
        "Genuine, accurate and verifiable documentation"
      ]
    },

    {
      title: "Visa Interview Preparation",
      description:
        "Some countries may require applicants to attend an interview as part of the student visa process. We provide personalised interview preparation to help applicants understand their application and communicate their intentions honestly, clearly and confidently.",
      bullets: [
        "Mock visa interview sessions",
        "Common student visa questions",
        "Guidance on explaining your programme choice",
        "Guidance on discussing your university and destination",
        "Financial sponsorship questions",
        "Career and post-study plan questions",
        "Document presentation guidance",
        "Communication and confidence-building support"
      ]
    },

    {
      title: "Application Follow-Up",
      description:
        "After submission, we provide guidance on tracking the application, responding to official requests and understanding the next steps in the visa process.",
      bullets: [
        "Tracking your application",
        "Responding to additional document requests",
        "Preparing for biometric appointments",
        "Understanding official visa correspondence",
        "Reviewing visa decisions and next steps",
        "Pre-departure preparation after approval"
      ]
    }
  ],

  areasSupported: {
    title: "Areas We Support",
    items: [
      "Student Visa Requirement Assessment",
      "Visa Application Form Guidance",
      "Personalised Document Checklists",
      "Application Timeline Planning",
      "Visa Fee and Submission Guidance",
      "Visa Appointment Scheduling Assistance",
      "Document Preparation",
      "Document Review",
      "Financial Documentation Guidance",
      "Sponsorship Documentation",
      "Bank Statement Guidance",
      "Study Plan and Personal Statement Guidance",
      "Certified Document Guidance",
      "Approved Translation Guidance",
      "Visa Interview Preparation",
      "Mock Visa Interviews",
      "Biometric Appointment Preparation",
      "Application Tracking",
      "Additional Document Requests",
      "Official Visa Correspondence",
      "Visa Decision and Next-Step Guidance",
      "Pre-Departure Preparation"
    ]
  },

  process: {
    title: "Our Visa Facilitation Process",

    steps: [
      {
        number: "01",
        title: "Initial Assessment",
        description:
          "We review your admission status, study destination, financial arrangements and personal circumstances."
      },
      {
        number: "02",
        title: "Document Checklist",
        description:
          "You receive a personalised list of the documents required for your application."
      },
      {
        number: "03",
        title: "Application Preparation",
        description:
          "We guide you in completing the application forms and organising your supporting documents."
      },
      {
        number: "04",
        title: "Application Review",
        description:
          "Your documents are reviewed for accuracy, completeness and consistency before submission."
      },
      {
        number: "05",
        title: "Interview Preparation",
        description:
          "Where an interview is required, we provide mock sessions and practical guidance."
      },
      {
        number: "06",
        title: "Submission and Follow-Up",
        description:
          "We guide you through submission, appointment scheduling and any additional requests from the relevant authority."
      }
    ]
  },

  whyChoose: {
    title: "Why Choose TravEd Consult?",

    items: [
      {
        title: "Country-Specific Guidance",
        description:
          "We provide guidance based on the student visa requirements of your chosen study destination."
      },
      {
        title: "Personalised Support",
        description:
          "Every application is different. Our recommendations are based on your academic, financial and personal circumstances."
      },
      {
        title: "Careful Document Review",
        description:
          "We help identify missing, unclear or inconsistent information before your application is submitted."
      },
      {
        title: "Interview Coaching",
        description:
          "Our mock interview sessions help you prepare to answer questions confidently and truthfully."
      },
      {
        title: "End-to-End Assistance",
        description:
          "We support you from admission and visa preparation through to pre-departure planning."
      }
    ]
  },

  importantNotice: {
    title: "Important Notice",
    paragraphs: [
      "TravEd Consult does not issue visas and cannot guarantee visa approval.",
      "Our role is to provide professional guidance and facilitate the application process.",
      "The final decision to approve or refuse a visa is made solely by the relevant embassy, consulate, immigration authority or authorised visa officer.",
      "Applicants are responsible for providing genuine, accurate and complete information throughout the application process."
    ]
  },

  cta: {
    title: "Begin Your Visa Application",
    description:
      "Do not allow avoidable errors or missing documents to delay your study-abroad plans. Book a visa facilitation consultation with TravEd Consult today.",
    buttonText: "Book a Visa Facilitation Consultation"
  }
},

{
  id: "university-selection-counselling-application-support",
  title: "University Selection Counselling & Application Support",
  tagline: "Find the Right University and Submit a Strong Application",
  heroDescription:
    "Choosing the right university is one of the most important decisions in your study-abroad journey. TravEd Consult helps you identify suitable universities and programmes based on your academic background, career goals, preferred destination and budget. Our support does not end with university selection. We guide you through the entire application process, helping you prepare and submit complete, accurate and competitive applications.",
  image: "/images/services/university-selection-counselling-application-support.jpg",

  introduction: {
    title: "University Selection Counselling & Application Support",
    paragraphs: [
      "Choosing the right university is one of the most important decisions in your study-abroad journey.",
      "At TravEd Consult, we help you identify suitable universities and programmes based on your academic background, career goals, preferred destination and budget.",
      "Our support does not end with university selection. We guide you through the entire application process, helping you prepare and submit complete, accurate and competitive applications."
    ]
  },

  offerSections: [
    {
      title: "Personalised Educational Counselling",
      description:
        "We begin by understanding your qualifications, interests, strengths and future plans. This allows us to recommend educational opportunities that are suitable for your profile.",
      bullets: [
        "Review of your academic background",
        "Discussion of your career and educational goals",
        "Identification of suitable fields of study",
        "Programme and qualification guidance",
        "Academic pathway planning"
      ]
    },

    {
      title: "University and Programme Search",
      description:
        "Our team researches universities and programmes that match your goals and admission profile. We help you compare the factors that are important when making your university decision.",
      bullets: [
        "Programme content and career outcomes",
        "Admission requirements",
        "Tuition fees and living expenses",
        "University location and campus environment",
        "Scholarships and financial aid opportunities",
        "Application deadlines",
        "Graduate employment prospects"
      ]
    },

    {
      title: "Study Destination Guidance",
      description:
        "Different countries offer different educational, financial and career opportunities. We provide clear information to help you choose a destination that suits your plans.",
      bullets: [
        "Comparison of study destinations",
        "Cost of education and accommodation",
        "Student lifestyle and cultural environment",
        "Programme duration and structure",
        "Post-study work opportunities",
        "General visa and immigration considerations"
      ]
    },

    {
      title: "University Application Support",
      description:
        "Once suitable universities have been selected, we guide you through each stage of the application process and help ensure that your application is complete, accurate and properly organised.",
      bullets: [
        "Creation of an application plan",
        "Review of admission requirements",
        "Online application completion",
        "Document preparation and review",
        "Personal statement and statement of purpose guidance",
        "CV and résumé preparation",
        "Recommendation letter guidance",
        "Application submission",
        "Deadline management",
        "Application status follow-up"
      ]
    },

    {
      title: "Scholarship and Financial Guidance",
      description:
        "We help students identify available scholarships, tuition discounts and funding opportunities offered by universities and other organisations.",
      bullets: [
        "Scholarship opportunity research",
        "Eligibility assessment",
        "Scholarship application guidance",
        "Financial document preparation guidance",
        "Tuition and living-cost planning"
      ]
    },

    {
      title: "Specialised Student Support",
      description:
        "We also provide guidance for students with specific educational needs and different academic pathways.",
      bullets: [
        "Transfer students",
        "Mature students",
        "Gap-year applicants",
        "Students changing programmes",
        "Diploma and pathway applicants",
        "Undergraduate applicants",
        "Postgraduate applicants",
        "Parents seeking guidance for their children"
      ]
    }
  ],

  areasSupported: {
    title: "Areas We Support",
    items: [
      "Educational Counselling",
      "Academic Background Assessment",
      "Career and Educational Goal Setting",
      "Field of Study Selection",
      "Programme and Qualification Guidance",
      "Academic Pathway Planning",
      "University Search",
      "Programme Search",
      "University Comparison",
      "Programme Comparison",
      "Study Destination Comparison",
      "Admission Requirement Review",
      "Tuition and Living-Cost Comparison",
      "Scholarship Research",
      "Financial Aid Guidance",
      "Application Planning",
      "Online Application Completion",
      "Document Preparation and Review",
      "Personal Statement Guidance",
      "Statement of Purpose Guidance",
      "CV and Résumé Preparation",
      "Recommendation Letter Guidance",
      "Application Submission",
      "Deadline Management",
      "Application Status Follow-Up",
      "Transfer Student Support",
      "Mature Student Support",
      "Gap-Year Applicant Support",
      "Programme Change Guidance",
      "Diploma and Pathway Applicant Support",
      "Undergraduate Application Support",
      "Postgraduate Application Support",
      "Parent Counselling and Guidance"
    ]
  },

  process: {
    title: "Our Process",

    steps: [
      {
        number: "01",
        title: "Initial Consultation",
        description:
          "We discuss your academic background, preferred programme, career goals, budget and study destination."
      },
      {
        number: "02",
        title: "Profile Assessment",
        description:
          "Our team reviews your qualifications and determines the programmes and institutions that may be suitable for you."
      },
      {
        number: "03",
        title: "University Matching",
        description:
          "We research and recommend universities based on your profile, preferences and admission prospects."
      },
      {
        number: "04",
        title: "Application Preparation",
        description:
          "We assist you with the required forms, documents and supporting statements."
      },
      {
        number: "05",
        title: "Application Submission",
        description:
          "Your application is carefully reviewed before submission to help reduce mistakes and delays."
      },
      {
        number: "06",
        title: "Admission Follow-Up",
        description:
          "We monitor the progress of your applications and guide you through additional requests, interviews or admission decisions."
      }
    ]
  },

  whyChoose: {
    title: "Why Choose TravEd Consult?",

    items: [
      {
        title: "Personalised Recommendations",
        description:
          "Every university recommendation is based on your individual academic profile, goals and financial situation."
      },
      {
        title: "Experienced Guidance",
        description:
          "Our consultants understand international admission processes and help you avoid common application mistakes."
      },
      {
        title: "Global Study Options",
        description:
          "We support applications to universities and colleges in the United States, Canada, the United Kingdom, Malta and other European destinations."
      },
      {
        title: "Complete Application Support",
        description:
          "We assist you from your first consultation through university selection, application submission and admission follow-up."
      },
      {
        title: "Honest and Practical Advice",
        description:
          "We provide realistic recommendations and help you make informed decisions about your education."
      }
    ]
  },

  importantNotice: {
    title: "Important Notice",
    paragraphs: [
      "University admission decisions are made by the respective universities and colleges.",
      "TravEd Consult provides counselling, application guidance and support but does not guarantee admission.",
      "Admission requirements, tuition fees, application deadlines, scholarship availability and programme details may change and should be confirmed with the relevant institution.",
      "Students remain responsible for providing accurate information and authentic documents throughout the application process.",
      "Visa and immigration decisions are made by the relevant authorities and are separate from university admission decisions."
    ]
  },

  cta: {
    title: "Start Your University Application",
    description:
      "Your study-abroad journey begins with selecting the right university and programme. Let TravEd Consult help you find suitable opportunities and submit your application with confidence.",
    buttonText: "Book a Consultation with TravEd Consult"
  }
},

{
  id: "career-counselling",
  title: "Career Counselling",
  tagline: "Make the Right Academic Choices for Your Future Career",
  heroDescription:
    "Career planning should not begin after graduation. It should begin when you are choosing what to study, where to study and the skills you need to develop. TravEd Consult provides personalised career counselling to help students, graduates and professionals make informed academic and career decisions.",
  image: "/images/services/career-counselling.jpg",

  introduction: {
    title: "Career Counselling",
    paragraphs: [
      "Career planning should not begin after graduation. It should begin when you are choosing what to study, where to study and the skills you need to develop.",
      "At TravEd Consult, we provide personalised career counselling to help students, graduates and professionals make informed academic and career decisions. We assess your interests, strengths, educational background and long-term goals before recommending suitable programmes, institutions and career pathways.",
      "Whether you are preparing to study abroad, entering the job market, changing careers or seeking professional growth, our counsellors will help you develop a clear and practical plan for your future."
    ]
  },

  offerSections: [
    {
      title: "Why Career Counselling Matters",
      description:
        "Choosing a university programme without considering your career goals can lead to uncertainty, limited employment opportunities or the need to change direction later. Our approach connects your education to your future career.",
      bullets: [
        "Which programmes match your interests and abilities",
        "The qualifications required for your preferred career",
        "The industries and occupations related to your chosen programme",
        "The skills employers expect from graduates",
        "The internship, certification and professional development opportunities available",
        "The possible career outcomes of studying in a particular country or institution"
      ]
    },

    {
      title: "Career Assessment and Planning",
      description:
        "We help you understand your strengths, interests, values and working preferences. This enables us to recommend career paths that fit your personality, abilities and long-term ambitions.",
      bullets: [
        "Career interest and strength assessment",
        "Short-term and long-term goal setting",
        "Career pathway mapping",
        "Academic and professional background review",
        "Skill-gap identification",
        "Professional development planning",
        "Industry and occupation research"
      ]
    },

    {
      title: "Programme and University Guidance",
      description:
        "The course you choose can have a major influence on your future opportunities. Our counsellors help you identify educational programmes that support your desired career.",
      bullets: [
        "Selecting a suitable field of study",
        "Comparing related programmes and specialisations",
        "Understanding programme requirements and career outcomes",
        "Choosing the right university or college",
        "Identifying suitable study destinations",
        "Exploring pathway, diploma, undergraduate and postgraduate options",
        "Evaluating programmes based on your background, goals and budget"
      ]
    },

    {
      title: "Internship and Work-Experience Guidance",
      description:
        "Practical experience can strengthen your employability and help you understand your chosen industry. We guide you in identifying opportunities that are relevant to your field of study and career direction.",
      bullets: [
        "Suitable internship opportunities",
        "Volunteer and community-service opportunities",
        "Graduate trainee programmes",
        "Industry-based projects",
        "Professional networking opportunities",
        "Work-experience options related to your field of study"
      ]
    },

    {
      title: "Job Search Support",
      description:
        "Entering the job market can be challenging without the right preparation. We provide practical support to help you present yourself professionally and approach your job search with confidence.",
      bullets: [
        "Professional CV and résumé preparation",
        "CV review and optimisation",
        "Personalised cover-letter development",
        "LinkedIn profile improvement",
        "Job-search strategy development",
        "Application organisation and tracking",
        "Guidance on professional communication and follow-ups"
      ]
    },

    {
      title: "Interview Preparation",
      description:
        "A strong application may help you secure an interview, but proper preparation is essential to perform well. Our interview support helps you understand how to communicate your experience, strengths and suitability effectively.",
      bullets: [
        "Mock interview sessions",
        "Guidance on common interview questions",
        "Behavioural interview preparation",
        "STAR-method training",
        "Personal presentation and communication coaching",
        "Role-specific interview preparation",
        "Constructive feedback and improvement recommendations",
        "Salary and employment-offer discussion guidance"
      ]
    },

    {
      title: "Professional Development",
      description:
        "Career growth requires continuous learning. We help you identify the qualifications, certifications and skills that can improve your professional profile.",
      bullets: [
        "Identification of in-demand skills",
        "Certification and short-course recommendations",
        "Digital and workplace skills development",
        "Networking and relationship-building strategies",
        "Personal branding guidance",
        "Industry insights and employment-market awareness",
        "Mentorship and professional-community recommendations"
      ]
    }
  ],

  whoWeSupport: {
    title: "Who We Support",
    items: [
      "Senior high school graduates choosing a university programme",
      "University students preparing for internships or employment",
      "Graduates entering the job market",
      "Study-abroad applicants choosing a programme and institution",
      "Professionals seeking career advancement",
      "Individuals considering a career change",
      "People returning to education after a period of employment",
      "Entrepreneurs seeking professional and business-development guidance"
    ]
  },

  areasSupported: {
    title: "Industries We Support",
    items: [
      "Technology and Information Technology",
      "Business, Management and Finance",
      "Healthcare and Public Health",
      "Education and Social Sciences",
      "Engineering and Construction",
      "Law and Public Administration",
      "Hospitality and Tourism",
      "Communication and Creative Industries",
      "Supply Chain and Logistics",
      "Entrepreneurship and Business Development"
    ]
  },

  process: {
    title: "Our Career Counselling Process",

    steps: [
      {
        number: "01",
        title: "Initial Consultation",
        description:
          "We begin by learning about your academic background, interests, experience, challenges and future ambitions."
      },
      {
        number: "02",
        title: "Profile Assessment",
        description:
          "We assess your strengths, preferences, qualifications and current skills to identify suitable academic and career options."
      },
      {
        number: "03",
        title: "Career Pathway Development",
        description:
          "We explore possible career directions and help you understand the education, qualifications and experience required for each option."
      },
      {
        number: "04",
        title: "Personalised Action Plan",
        description:
          "You receive a clear plan outlining the programmes, skills, certifications, internships or job-search activities you should prioritise."
      },
      {
        number: "05",
        title: "Ongoing Guidance",
        description:
          "We continue to support you as you make decisions, apply for opportunities and progress towards your academic and professional goals."
      }
    ]
  },

  whyChoose: {
    title: "Why Choose TravEd Consult?",

    items: [
      {
        title: "Personalised Guidance",
        description:
          "We understand that every student and professional has a different background, ambition and situation. Our recommendations are based on your individual profile rather than a general approach."
      },
      {
        title: "Education and Career Alignment",
        description:
          "We help you choose educational programmes that connect directly with your long-term career goals."
      },
      {
        title: "Practical Support",
        description:
          "Our guidance goes beyond advice. We help you prepare the documents, strategies and action plans needed to move forward."
      },
      {
        title: "International Education Knowledge",
        description:
          "Our study-abroad experience enables us to advise you on programmes, institutions and destinations that may support your academic and professional development."
      },
      {
        title: "End-to-End Assistance",
        description:
          "From programme selection and university applications to career planning and job-search preparation, TravEd Consult supports you throughout your journey."
      }
    ]
  },

  importantNotice: {
    title: "Important Notice",
    paragraphs: [
      "Career counselling provides guidance and recommendations based on the information available about your interests, qualifications, skills and goals.",
      "Career outcomes can vary depending on individual circumstances, academic performance, labour-market conditions, employer requirements and other factors.",
      "University admission, employment, internship and professional opportunities are subject to the requirements and decisions of the relevant institutions and organisations.",
      "Students and professionals remain responsible for making their final educational and career decisions."
    ]
  },

  cta: {
    title: "Start Planning Your Future Today",
    description:
      "Your career journey begins with the decisions you make today. Let TravEd Consult help you choose the right programme, develop the right skills and build a clear path towards your professional goals.",
    buttonText: "Book a Career Counselling Consultation"
  }
},

{
  id: "travEd-global-education-fair",
  title: "TravEd Global Education Fair",
  tagline: "Your Gateway to International Education",
  heroDescription:
    "The TravEd Global Education Fair (TGEF) is a major international education and student-recruitment event created to connect African students, graduates, parents and professionals directly with universities, colleges and education providers from around the world.",
  image: "/images/services/traved-global-education-fair.jpg",

  introduction: {
    title: "TravEd Global Education Fair",
    paragraphs: [
      "The TravEd Global Education Fair (TGEF) is a major international education and student-recruitment event created to connect African students, graduates, parents and professionals directly with universities, colleges and education providers from around the world.",
      "Organised by TravEd Consult, the fair provides a trusted platform where prospective students can explore international study opportunities, receive personalised admission guidance and interact directly with authorised university representatives.",
      "TGEF is designed to simplify the process of studying abroad by bringing essential information and services together in one place. Participants can learn about available programmes, admission requirements, scholarships, tuition fees, study destinations, visa processes, accommodation and career opportunities after graduation.",
      "Rather than relying only on online information, students and parents will have the opportunity to ask questions, compare institutions and receive guidance based on their academic background, career goals and financial circumstances.",
      "The fair will also serve as an education networking platform where universities, schools, training institutions, government agencies, sponsors and other stakeholders can develop partnerships that expand access to international education for African students."
    ]
  },

  offerSections: [
    {
      title: "Project Overview",
      description:
        "The TravEd Global Education Fair will bring together international universities, colleges, pathway providers, language schools, vocational institutions and other education organisations interested in recruiting qualified students from Ghana and across Africa.",
      bullets: [
        "International universities",
        "Colleges",
        "Pathway providers",
        "Language schools",
        "Vocational institutions",
        "Other international education organisations",
        "Programme presentations",
        "Admission requirement explanations",
        "Direct engagement with prospective students",
        "Undergraduate opportunities",
        "Postgraduate opportunities",
        "Diploma opportunities",
        "Pathway opportunities",
        "Professional study opportunities",
        "Vocational study opportunities",
        "Student counselling",
        "Admission assessments",
        "Scholarship information sessions",
        "Visa education",
        "Seminars",
        "Networking activities"
      ]
    },

    {
      title: "The Vision",
      description:
        "The vision of the TravEd Global Education Fair is to become one of Africa’s most trusted international education platforms, connecting qualified students with credible institutions and helping them make informed decisions about their academic futures.",
      bullets: [
        "Make international education information more accessible",
        "Make international education information more practical",
        "Make international education information more transparent",
        "Provide students and families with direct access to institutions",
        "Help students navigate complex admission processes",
        "Strengthen educational relationships between African institutions and universities around the world"
      ]
    },

    {
      title: "The Need for the Fair",
      description:
        "Many African students want to study abroad but struggle to find reliable and complete information about universities, admission requirements, scholarships, tuition fees and visa processes. TGEF provides a structured environment where students and parents can access professional guidance and compare their options before making decisions.",
      bullets: [
        "Reliable university information",
        "Complete admission information",
        "Scholarship information",
        "Tuition fee information",
        "Visa-process information",
        "Programme suitability guidance",
        "Deadline awareness",
        "Complete application guidance",
        "Financial planning information",
        "Country comparison",
        "University comparison",
        "Accommodation comparison",
        "Direct access to education experts",
        "Direct access to authorised representatives"
      ]
    },

    {
      title: "Connecting Students with International Institutions",
      description:
        "The primary objective of TGEF is to create a direct connection between African students and credible international universities, colleges and education providers.",
      bullets: [
        "Interaction with admissions officers",
        "Interaction with international recruitment representatives",
        "Interaction with education specialists",
        "Programme information",
        "Institutional requirement information",
        "Direct institutional engagement"
      ]
    },

    {
      title: "Direct Access to Admissions Information",
      description:
        "The fair will help students understand entry requirements, application deadlines, tuition fees, programme structures and available study pathways.",
      bullets: [
        "Entry requirements",
        "Application deadlines",
        "Tuition fees",
        "Programme structures",
        "Study pathways",
        "Preliminary qualification guidance"
      ]
    },

    {
      title: "Scholarship and Funding Opportunities",
      description:
        "Participating institutions will be encouraged to present available scholarships, tuition discounts, bursaries, graduate assistantships and other financial-support opportunities.",
      bullets: [
        "Merit scholarships",
        "Need-based assistance",
        "Tuition discounts",
        "Bursaries",
        "Graduate assistantships",
        "Research assistantships",
        "External sponsorship opportunities",
        "Scholarship eligibility guidance",
        "Scholarship application procedures",
        "Supporting-document guidance"
      ]
    },

    {
      title: "Visa Awareness",
      description:
        "TGEF will provide accurate and responsible information about student visa requirements, financial preparation, supporting documents, interviews and common application mistakes.",
      bullets: [
        "Student visa requirements",
        "Financial preparation",
        "Supporting documents",
        "Visa interviews",
        "Common application mistakes",
        "Proof of funds",
        "Study plans",
        "Sponsorship",
        "Biometrics",
        "Common reasons for delays",
        "Common reasons for refusals"
      ]
    },

    {
      title: "Study-Abroad Decision Support",
      description:
        "The fair will help students compare countries, programmes and institutions based on academic suitability, cost, career outcomes and personal preferences.",
      bullets: [
        "Country comparison",
        "Programme comparison",
        "Institution comparison",
        "Academic suitability",
        "Education costs",
        "Career outcomes",
        "Personal preferences",
        "Long-term career alignment"
      ]
    },

    {
      title: "International Education Partnerships",
      description:
        "TGEF will create opportunities for international institutions to develop relationships with Ghanaian schools, universities, training centres, education consultants and other stakeholders.",
      bullets: [
        "Student recruitment partnerships",
        "Institutional visits",
        "Pathway agreements",
        "Exchange programmes",
        "Research collaboration",
        "Other educational initiatives"
      ]
    },

    {
      title: "University and College Exhibition",
      description:
        "Participating institutions will have dedicated exhibition spaces where they can display programme information, admission materials and promotional resources.",
      bullets: [
        "Dedicated exhibition spaces",
        "Programme information",
        "Admission materials",
        "Promotional resources",
        "Direct interaction with students",
        "Direct interaction with parents",
        "Programme questions",
        "Tuition questions",
        "Accommodation questions",
        "Student-support questions",
        "Application-procedure questions"
      ]
    },

    {
      title: "Direct Interaction with Admissions Officers",
      description:
        "One of the main advantages of TGEF will be direct access to university and college representatives, allowing students to receive first-hand information from the institutions.",
      bullets: [
        "Direct access to university representatives",
        "Direct access to college representatives",
        "First-hand programme information",
        "First-hand admission information",
        "Opportunity to ask detailed questions",
        "Reduced dependence on unofficial information sources"
      ]
    },

    {
      title: "Preliminary Admission Assessments",
      description:
        "Students who bring the relevant academic documents may receive an initial assessment of their admission prospects.",
      bullets: [
        "Qualification review",
        "Programme-option guidance",
        "Identification of additional documents",
        "Identification of additional requirements",
        "Initial admission assessment"
      ]
    },

    {
      title: "On-the-Spot Applications and Conditional Offers",
      description:
        "Selected participating institutions may accept applications during the event. Where permitted by the university, qualified applicants may receive application-fee waivers, priority application reviews or conditional admission decisions.",
      bullets: [
        "On-the-spot applications",
        "Application-fee waivers where offered",
        "Priority application reviews where offered",
        "Conditional admission decisions where offered",
        "Document review",
        "Institutional eligibility assessment"
      ]
    },

    {
      title: "Study Destination Seminars",
      description:
        "Country-specific sessions will help students compare major international education destinations and understand the academic and practical differences between them.",
      bullets: [
        "Education systems",
        "Programme duration",
        "Tuition fees",
        "Living expenses",
        "Accommodation",
        "Student experience",
        "Climate",
        "Post-study opportunities"
      ]
    },

    {
      title: "Programme and Career Sessions",
      description:
        "Students will receive guidance on selecting programmes that align with their abilities, interests and long-term career plans.",
      bullets: [
        "Programme selection",
        "Abilities and interests assessment",
        "Career-plan alignment",
        "Professional qualifications",
        "Employment prospects",
        "Further education opportunities",
        "Academic-to-career pathway guidance"
      ]
    },

    {
      title: "One-on-One Counselling",
      description:
        "Registered students may have access to personalised counselling sessions with TravEd Consult advisers or participating institution representatives.",
      bullets: [
        "Qualifications review",
        "Preferred programme discussion",
        "Career-goal discussion",
        "Study-destination discussion",
        "Budget considerations",
        "Personalised study guidance"
      ]
    },

    {
      title: "Parent Advisory Sessions",
      description:
        "Special sessions will be organised for parents and guardians to discuss tuition planning, sponsorship, accommodation, student safety and preparing young people for independent living abroad.",
      bullets: [
        "Tuition planning",
        "Sponsorship",
        "Accommodation",
        "Student safety",
        "Student welfare",
        "Independent living preparation"
      ]
    },

    {
      title: "Application Clinics",
      description:
        "TravEd Consult may organise application-support stations where students can receive guidance on applications and supporting documents.",
      bullets: [
        "Application forms",
        "Personal statements",
        "Academic CVs",
        "Recommendation letters",
        "Supporting documents"
      ]
    },

    {
      title: "Networking and Partnership Meetings",
      description:
        "Participating institutions, schools and education organisations will have opportunities to discuss partnerships, student recruitment arrangements and other areas of collaboration.",
      bullets: [
        "Institutional networking",
        "School partnerships",
        "Student recruitment arrangements",
        "Education collaboration",
        "Partnership discussions"
      ]
    },

    {
      title: "Pre-Event Activities",
      description:
        "Before the fair, TravEd Consult may organise activities designed to prepare students, institutions and sponsors for meaningful participation.",
      bullets: [
        "Online information sessions",
        "School visits",
        "Career seminars",
        "Student-registration campaigns",
        "Academic-document preparation guidance",
        "Guidance on questions to ask university representatives",
        "Event schedule information",
        "Audience profile information",
        "Exhibition arrangements",
        "Promotional opportunities"
      ]
    },

    {
      title: "Post-Event Support",
      description:
        "The value of TGEF will continue after the exhibition ends. TravEd Consult will follow up with interested students and provide guidance as they progress through their international education journey.",
      bullets: [
        "University selection",
        "Application support",
        "Supporting-document guidance",
        "Visa facilitation",
        "Accommodation guidance",
        "Travel arrangements",
        "Student follow-up",
        "Structured institutional follow-up subject to consent and applicable data-protection requirements",
        "Post-event surveys",
        "Event reports",
        "Attendance evaluation",
        "Student-interest analysis",
        "Application-outcome tracking",
        "Partnership-outcome evaluation"
      ]
    }
  ],

  targetAudience: {
    title: "Who Should Attend?",
    items: [
      {
        title: "Senior High School Graduates",
        description:
          "School leavers seeking undergraduate, foundation, diploma, vocational or pathway programmes abroad."
      },
      {
        title: "University Graduates",
        description:
          "Graduates seeking master’s degrees, postgraduate diplomas, professional programmes and research opportunities."
      },
      {
        title: "Current University Students",
        description:
          "Students interested in transferring to another institution, participating in an exchange programme or continuing their education abroad."
      },
      {
        title: "Working Professionals",
        description:
          "Professionals considering postgraduate education, executive programmes, certifications or career changes."
      },
      {
        title: "Parents and Guardians",
        description:
          "Parents seeking reliable information about tuition costs, accommodation, safety, student welfare, visa processes and financial responsibilities."
      },
      {
        title: "Schools and Training Institutions",
        description:
          "Senior high schools, universities, language centres, vocational institutions and professional training providers seeking international progression opportunities for their students."
      },
      {
        title: "Education and Career Professionals",
        description:
          "School counsellors, teachers, education consultants and career advisers seeking international admissions knowledge and institutional connections."
      }
    ]
  },

  participatingInstitutions: {
    title: "Participating Institutions",
    items: [
      "Universities and colleges",
      "Community colleges",
      "Technical and vocational institutions",
      "Pathway and foundation providers",
      "Language schools",
      "Boarding schools",
      "Professional certification organisations",
      "Student accommodation providers",
      "Education financing organisations",
      "Testing and credential-evaluation organisations",
      "Scholarship and funding organisations"
    ],
    participationFormats: [
      "Physical participation",
      "Virtual participation",
      "Participation through an approved representative"
    ]
  },

  documentsToBring: {
    title: "Documents Students Should Bring",
    description:
      "Students seeking admission guidance or an initial assessment are encouraged to attend with relevant academic and identification documents.",
    items: [
      "Academic transcripts",
      "Academic certificates",
      "Examination results",
      "Curriculum vitae",
      "Passport biodata page",
      "Previous admission documents",
      "Language-test documents",
      "Other relevant academic documents",
      "Other relevant identification documents"
    ],
    notice:
      "Students who do not yet have all their documents will still be able to attend the fair, learn about opportunities and receive guidance on what they need to prepare. Original documents should be protected carefully. Copies or secure digital versions may be used for initial discussions unless an institution specifically requests otherwise."
  },

  benefits: {
    students: {
      title: "Benefits for Students",
      items: [
        "Direct access to international institutions",
        "Personalised programme guidance",
        "Scholarship information",
        "Better application preparation",
        "Visa awareness",
        "Time and cost savings",
        "Continued support after the fair"
      ]
    },

    parents: {
      title: "Benefits for Parents",
      items: [
        "Reliable information about international education costs",
        "Accommodation information",
        "Student safety information",
        "Visa requirements",
        "Student support information",
        "Direct institution comparison",
        "Opportunity to ask questions about student welfare",
        "Understanding the complete cost of studying abroad",
        "Understanding tuition fees",
        "Understanding accommodation costs",
        "Understanding insurance costs",
        "Understanding transportation costs",
        "Understanding visa fees",
        "Understanding living expenses"
      ]
    },

    institutions: {
      title: "Benefits for Universities and Education Providers",
      items: [
        "Access to qualified African students",
        "Increased brand visibility",
        "Direct student engagement",
        "Application generation",
        "Market insights",
        "Partnership development",
        "Expanded international enrolment"
      ]
    }
  },

  exhibitorOpportunities: {
    title: "Exhibitor Opportunities",
    description:
      "Universities, colleges and education organisations can participate as exhibitors and receive a dedicated platform to interact with attendees.",
    potentialBenefits: [
      "Exhibition space",
      "Institutional branding",
      "Presentation opportunities",
      "Access to registered student profiles",
      "Scheduled counselling sessions",
      "Inclusion in event promotions",
      "Information sessions",
      "Preliminary assessments",
      "Partnership meetings"
    ],
    requirements: [
      "Participating institutions must provide accurate information about their programmes",
      "Participating institutions must provide accurate fee information",
      "Participating institutions must provide accurate scholarship information",
      "Participating institutions must provide accurate admission requirements"
    ]
  },

  sponsorship: {
    title: "Sponsorship Opportunities",
    description:
      "The TravEd Global Education Fair offers sponsorship opportunities to organisations that want to support education, youth development and global mobility while gaining meaningful visibility.",
    suitableSponsors: [
      "Financial institutions",
      "Telecommunications companies",
      "Airlines",
      "Insurance companies",
      "Accommodation providers",
      "Technology companies",
      "Employers",
      "Media organisations",
      "Education-focused institutions"
    ],
    supportAreas: [
      "Event venue",
      "Technology",
      "Student transportation",
      "Media promotion",
      "Scholarship sessions",
      "Counselling facilities",
      "Event materials",
      "Other operational needs"
    ],
    categories: [
      {
        title: "Title Sponsor",
        description:
          "The title sponsor will receive leading brand visibility and recognition as the principal partner of the event.",
        benefits: [
          "Prominent branding",
          "Speaking opportunities",
          "Priority exhibition space",
          "Media recognition",
          "Inclusion in major promotional materials"
        ]
      },
      {
        title: "Platinum and Gold Sponsors",
        description:
          "These packages will provide strong visibility through event branding, exhibition opportunities, digital promotion and selected programme participation.",
        benefits: [
          "Event branding",
          "Exhibition opportunities",
          "Digital promotion",
          "Selected programme participation"
        ]
      },
      {
        title: "Session Sponsor",
        description:
          "An organisation may sponsor a specific session such as the Scholarship Forum, Visa Information Session, Parent Advisory Session or Career and Programme Seminar.",
        benefits: [
          "Association with a selected event session",
          "Session-level visibility",
          "Participation in selected programme activities"
        ]
      },
      {
        title: "Student Support Sponsor",
        description:
          "A sponsor may support student participation through transportation, application-fee assistance, scholarship awards, event materials or counselling resources.",
        benefits: [
          "Student transportation support",
          "Application-fee assistance",
          "Scholarship awards",
          "Event materials",
          "Counselling resources"
        ]
      },
      {
        title: "Media Partner",
        description:
          "Media organisations can support the promotion and coverage of the event through interviews, advertisements, event reporting and digital content.",
        benefits: [
          "Interviews",
          "Advertisements",
          "Event reporting",
          "Digital content"
        ]
      },
      {
        title: "Travel and Hospitality Partner",
        description:
          "Airlines, hotels, transport providers and travel organisations can support the movement and accommodation of participating representatives.",
        benefits: [
          "Representative transportation support",
          "Representative accommodation support",
          "Travel partnership opportunities",
          "Hospitality partnership opportunities"
        ]
      }
    ],
    sponsorBenefits: [
      "Access to students",
      "Access to parents",
      "Access to professionals",
      "Access to schools",
      "Access to international education institutions",
      "Logo placement",
      "Exhibition space",
      "Stage recognition",
      "Speaking opportunities",
      "Media mentions",
      "Branded materials",
      "Digital promotion",
      "Engagement with registered participants",
      "Positioning as a supporter of education",
      "Positioning as a supporter of youth advancement",
      "Positioning as a supporter of international opportunities"
    ]
  },

  partnershipOpportunities: {
    title: "Partnership Opportunities",
    description:
      "Beyond sponsorship, TGEF will encourage long-term partnerships with schools, universities, government agencies, youth organisations and professional bodies.",
    partners: [
      {
        title: "School Partners",
        description:
          "May assist with student mobilisation and career guidance."
      },
      {
        title: "Institutional Partners",
        description:
          "May support programme delivery, student recruitment and educational collaboration."
      },
      {
        title: "Professional Bodies, Youth Organisations and Community Groups",
        description:
          "May help identify participants who can benefit from international education opportunities."
      }
    ]
  },

  eventFormat: {
    title: "Event Delivery Format",
    formats: [
      {
        title: "Physical",
        description:
          "Physical editions will allow face-to-face interaction, document assessment, exhibitions and networking."
      },
      {
        title: "Virtual",
        description:
          "Virtual participation may enable institutions that cannot travel to Ghana to conduct live presentations, counselling sessions and online engagement."
      },
      {
        title: "Hybrid",
        description:
          "A hybrid format can combine both approaches and extend access to participants outside the event city."
      }
    ]
  },

  marketingAndPublicity: {
    title: "Marketing and Publicity",
    description:
      "The fair will be promoted through a combination of digital, media, educational and partnership channels.",
    channels: [
      "Social media",
      "Radio",
      "Digital advertising",
      "School outreach",
      "University networks",
      "Professional associations",
      "Education-focused partnerships"
    ],
    promotionalActivities: [
      "Interviews",
      "Countdown campaigns",
      "Student testimonials",
      "Institution profiles",
      "Programme highlights",
      "Educational content"
    ],
    partnerPromotion:
      "Partners, exhibitors and sponsors will also be encouraged to promote their participation through their official platforms."
  },

  expectedOutcomes: {
    title: "Expected Outcomes",
    items: [
      "Increased access to reliable international education information",
      "More students identifying suitable study opportunities",
      "Generation of student applications",
      "Support for institutional recruitment",
      "Improved awareness of scholarships",
      "Creation of new education partnerships",
      "Strengthened TravEd Consult visibility",
      "Strengthened TravEd Consult credibility",
      "Stronger relationships with international institutions",
      "Development of a recognised annual platform for international student recruitment and education networking in Africa"
    ]
  },

  process: {
    title: "The TGEF Journey",

    steps: [
      {
        number: "01",
        title: "Register to Attend",
        description:
          "Students, graduates, professionals and parents register to participate in the TravEd Global Education Fair and explore international education opportunities."
      },
      {
        number: "02",
        title: "Prepare Your Documents",
        description:
          "Registered participants may receive guidance on preparing academic documents and identifying the questions they should ask university representatives."
      },
      {
        number: "03",
        title: "Explore Institutions",
        description:
          "Participants visit university and college exhibitions, interact with representatives and explore programmes, destinations and admission requirements."
      },
      {
        number: "04",
        title: "Receive Guidance",
        description:
          "Students can access counselling, preliminary admission assessments, scholarship information, visa education, programme sessions and application clinics."
      },
      {
        number: "05",
        title: "Compare Your Options",
        description:
          "Participants compare institutions, programmes, destinations, costs, scholarships, accommodation and career opportunities before making informed decisions."
      },
      {
        number: "06",
        title: "Apply and Follow Up",
        description:
          "Students who decide to proceed can receive continued guidance on university selection, applications, supporting documents, visa facilitation, accommodation and travel arrangements."
      }
    ]
  },

  whyChoose: {
    title: "Why Attend TGEF?",

    items: [
      {
        title: "Direct Access",
        description:
          "Meet representatives from international universities, colleges and education providers directly rather than relying entirely on online searches."
      },
      {
        title: "Personalised Guidance",
        description:
          "Discuss your qualifications, preferred programme, career goals, study destination and available budget with education professionals."
      },
      {
        title: "Compare Multiple Options",
        description:
          "Explore several institutions, programmes and destinations during one event, making the research process more efficient."
      },
      {
        title: "Scholarship Information",
        description:
          "Learn directly from participating institutions about available scholarships, tuition discounts and other financial opportunities."
      },
      {
        title: "Application Support",
        description:
          "Receive guidance on application forms, personal statements, academic CVs, recommendation letters and supporting documents."
      },
      {
        title: "Continued Support",
        description:
          "TravEd Consult provides follow-up counselling and application support to students who decide to proceed after the fair."
      }
    ]
  },

  importantNotice: {
    title: "Important Notice",
    paragraphs: [
      "Preliminary admission assessments remain subject to the institution’s official application and admission process.",
      "On-the-spot admission will depend on the student providing the required documents and meeting the institution’s conditions.",
      "TravEd Consult will not guarantee an admission decision on behalf of any institution.",
      "Scholarship availability remains subject to the conditions and decisions of the institution or funding organisation.",
      "TravEd Consult does not guarantee scholarships or visa approval.",
      "Visa decisions are made solely by the relevant embassy, consulate or immigration authority.",
      "Participating institutions are required to provide accurate information about their programmes, fees, scholarships and admission requirements.",
      "Participating institutions may receive structured follow-up information on students who have expressed genuine interest, subject to consent and applicable data-protection requirements.",
      "TravEd Consult's responsibility is to create access, provide professional guidance and help participants make informed educational decisions."
    ]
  },

  cta: {
    title: "Take the Next Step Towards Your International Education Journey",
    description:
      "Students, graduates, professionals and parents are invited to register for the TravEd Global Education Fair and explore international education opportunities. Registration may provide access to institution exhibitions, counselling sessions, seminars, scholarship information and other selected event activities.",
    buttonText: "Register to Attend"
  },

  exhibitorCta: {
    title: "Become an Exhibitor",
    description:
      "Universities, colleges and education providers seeking to recruit qualified African students are invited to participate in the TravEd Global Education Fair and connect directly with their next generation of international students.",
    buttonText: "Become an Exhibitor"
  },

  sponsorCta: {
    title: "Become a Sponsor or Partner",
    description:
      "Organisations interested in supporting global education, youth development and international opportunities can participate through sponsorship, institutional partnerships, exhibitions or media collaboration.",
    buttonText: "Become a Sponsor or Partner"
  }
},

{
  id: "research-academic-assistance",
  title: "Research and Academic Assistance",
  tagline: "Professional Guidance for Better Academic Work",
  heroDescription:
    "Academic research and assignments can become challenging when students are uncertain about the topic, structure, research process, referencing style or expectations of their institution.",
  image: "/images/services/research-academic-assistance.jpg",

  introduction: {
    title: "Research and Academic Assistance",
    paragraphs: [
      "TravEd Consult provides ethical research and academic assistance to students, researchers and professionals who need guidance in developing well-structured, properly researched and clearly presented academic work.",
      "Our service is designed to help clients understand their assignments, strengthen their research skills and improve the quality of their work.",
      "We provide tutoring, research guidance, document review, editing and practical support that enables clients to produce and defend their own work confidently."
    ]
  },

  offerSections: [
    {
      title: "Assignment Guidance and Academic Tutoring",
      description:
        "TravEd Consult helps students interpret assignment questions, identify the main requirements and develop an appropriate approach to the task. We explain the topic, guide the student in finding credible sources and help create a clear structure for the assignment.",
      bullets: [
        "Assignment interpretation",
        "Identification of key requirements",
        "Credible source guidance",
        "Assignment structure development",
        "Draft review and feedback"
      ]
    },

    {
      title: "Research Topic Development",
      description:
        "We help students refine their areas of interest into clear, relevant and manageable research topics. During the topic-development process, we consider the student's programme, academic level, available time, research interests, access to information and the requirements of the institution.",
      bullets: [
        "Research topic refinement",
        "Research question development",
        "Research objectives",
        "Hypotheses",
        "Problem statements"
      ]
    },

    {
      title: "Research Proposal Assistance",
      description:
        "TravEd Consult provides guidance in developing proposals for undergraduate projects, postgraduate dissertations, theses, institutional research and professional studies.",
      bullets: [
        "Background of the study",
        "Problem statement",
        "Research objectives",
        "Research questions",
        "Justification",
        "Scope",
        "Literature review",
        "Methodology",
        "Work plan",
        "Proposed budget"
      ]
    },

    {
      title: "Literature Review Guidance",
      description:
        "We guide students in searching for relevant books, journal articles, reports, policies and other credible academic sources.",
      bullets: [
        "Organising literature into themes",
        "Comparing different viewpoints",
        "Identifying research gaps",
        "Connecting previous studies to current research",
        "Paraphrasing and analysis",
        "Appropriate referencing"
      ]
    },

    {
      title: "Research Methodology Support",
      description:
        "TravEd Consult helps clients understand and select appropriate research designs, populations, sampling methods, data-collection techniques and analysis procedures.",
      bullets: [
        "Quantitative research guidance",
        "Qualitative research guidance",
        "Mixed-method research guidance",
        "Questionnaires",
        "Sampling",
        "Variables",
        "Hypotheses",
        "Statistical analysis",
        "Interview guides",
        "Focus-group questions",
        "Coding",
        "Thematic analysis"
      ]
    },

    {
      title: "Questionnaire and Interview Guide Development",
      description:
        "We help researchers develop questionnaires, interview guides, observation checklists and other data-collection tools that align with their research objectives.",
      bullets: [
        "Question clarity",
        "Question relevance",
        "Question ordering",
        "Bias review",
        "Pilot testing guidance",
        "Participant consent guidance",
        "Ethical data-collection guidance"
      ]
    },

    {
      title: "Data Analysis Assistance",
      description:
        "TravEd Consult provides guidance with quantitative and qualitative data analysis and helps researchers understand, discuss and defend their findings.",
      bullets: [
        "Data cleaning",
        "Data coding",
        "Descriptive statistics",
        "Reliability testing",
        "Correlation",
        "Regression",
        "Statistical result interpretation",
        "Qualitative coding",
        "Theme identification"
      ]
    },

    {
      title: "Dissertation and Thesis Support",
      description:
        "TravEd Consult provides stage-by-stage support from topic selection and proposal development through data analysis, discussion, conclusion and final review.",
      bullets: [
        "Topic selection",
        "Proposal development",
        "Data analysis",
        "Discussion",
        "Conclusion",
        "Final review",
        "Supervisor comment review",
        "Correction guidance"
      ]
    },

    {
      title: "Academic Editing and Proofreading",
      description:
        "Our academic editing service focuses on improving clarity, readability and presentation while preserving the writer's original meaning.",
      bullets: [
        "Grammar",
        "Spelling",
        "Punctuation",
        "Sentence structure",
        "Paragraph development",
        "Headings",
        "Consistency",
        "Academic tone"
      ]
    },

    {
      title: "Referencing and Citation Support",
      description:
        "TravEd Consult helps students understand and apply commonly used referencing styles and provides guidance on citations, footnotes and reference lists.",
      bullets: [
        "APA",
        "Harvard",
        "MLA",
        "Chicago",
        "OSCOLA",
        "In-text citations",
        "Footnotes",
        "Reference lists",
        "Bibliographies",
        "Citation consistency"
      ]
    },

    {
      title: "Plagiarism Awareness and Originality Guidance",
      description:
        "We educate students on proper paraphrasing, quotation, citation and responsible use of academic sources.",
      bullets: [
        "Responsible paraphrasing",
        "Quotation",
        "Citation",
        "Academic source usage",
        "Originality guidance"
      ]
    },

    {
      title: "Research Presentation and Defence Preparation",
      description:
        "TravEd Consult helps students organise their findings into a clear and professional presentation and prepare for oral defence or viva sessions.",
      bullets: [
        "Presentation structure",
        "Slide development",
        "Time management",
        "Research problem explanation",
        "Methodology explanation",
        "Findings",
        "Conclusions",
        "Recommendations",
        "Mock defence sessions",
        "Likely defence questions"
      ]
    },

    {
      title: "Academic and Professional Reports",
      description:
        "Our support is also available to professionals, businesses, NGOs and institutions that need assistance with research-based reports.",
      bullets: [
        "Market-research reports",
        "Needs assessments",
        "Project reports",
        "Policy briefs",
        "Feasibility studies",
        "Monitoring and evaluation reports",
        "Training reports",
        "Organisational research"
      ]
    }
  ],

  areasSupported: {
    title: "Areas We Support",
    items: [
      "Business",
      "Management",
      "Education",
      "Social Sciences",
      "Public Administration",
      "Economics",
      "Finance",
      "Marketing",
      "Communication",
      "Development Studies",
      "Tourism",
      "Law",
      "Information Technology",
      "Public Health"
    ]
  },

  process: {
    title: "Our Academic Support Process",

    steps: [
      {
        number: "01",
        title: "Initial Consultation",
        description:
          "We begin by understanding the nature of the assignment or research project, the academic level, topic, deadline and institutional requirements."
      },
      {
        number: "02",
        title: "Academic Assessment",
        description:
          "We review the available materials to identify what has been completed, what is missing and the areas that require improvement."
      },
      {
        number: "03",
        title: "Work Plan",
        description:
          "A clear work plan is developed based on the assignment requirements and available deadline."
      },
      {
        number: "04",
        title: "Guidance and Development",
        description:
          "Our consultants provide explanations, recommendations and feedback throughout the process."
      },
      {
        number: "05",
        title: "Review and Quality Check",
        description:
          "The completed draft is reviewed for structure, consistency, clarity, referencing and alignment with the stated requirements."
      },
      {
        number: "06",
        title: "Final Preparation",
        description:
          "The client receives guidance on final formatting, submission requirements, presentation preparation or supervisor review."
      }
    ]
  },

  whyChoose: {
    title: "Why Choose TravEd Consult?",

    items: [
      {
        title: "Personalised Academic Guidance",
        description:
          "Every assignment and research project has different requirements. Our support is based on the client's topic, academic level, institution and specific challenges."
      },
      {
        title: "Focus on Understanding",
        description:
          "We do not only identify errors. We explain why changes are needed and help clients understand the academic principles behind the work."
      },
      {
        title: "Structured Support",
        description:
          "Our process helps students move from a broad idea or difficult assignment to a clear and manageable academic plan."
      },
      {
        title: "Confidentiality",
        description:
          "Documents and information shared for academic support are treated with care and used only for the agreed service."
      },
      {
        title: "Ethical Academic Assistance",
        description:
          "We support learning, research development and academic improvement while respecting institutional rules and academic-integrity standards."
      },
      {
        title: "Comprehensive Service",
        description:
          "Our support can cover topic development, proposals, literature reviews, methodology, data analysis, editing, referencing and defence preparation."
      }
    ]
  },

  importantNotice: {
    title: "Important Academic Integrity Notice",
    paragraphs: [
      "TravEd Consult provides educational guidance, tutoring, editing, research support and academic-development services.",
      "We do not write examinations, impersonate students, fabricate research participants, invent data, falsify results or complete assessed work for dishonest submission.",
      "Clients remain responsible for the originality, accuracy and final submission of their academic work.",
      "All academic services must comply with the rules, policies and ethical requirements of the client's school, university, professional body or institution.",
      "TravEd Consult may refuse any request that involves plagiarism, fabricated evidence, false data or academic misconduct."
    ]
  },

  cta: {
    title: "Improve Your Academic Work with Professional Guidance",
    description:
      "Whether you need help understanding an assignment, developing a research proposal, analysing data, reviewing a dissertation or preparing for a research defence, TravEd Consult is ready to guide you through the process.",
    buttonText: "Contact TravEd Consult"
  }
}
  // 🔥 ADD YOUR OTHER 5 SERVICES HERE
];